/**
 * POLISH DETAIL MODAL - REDESIGNED!
 * 
 * Features:
 * - Responsive two-column layout (desktop) / stacked (mobile)
 * - Photo caption & credit synced with carousel
 * - Collapsible sections for Tags, Notes, Usage, Toggles
 * - Photo editing mode
 * - All existing functionality preserved
 * - Body scroll lock to prevent scroll bleed
 */

import React, { useState, useEffect } from 'react';
import { X, Heart, Info, Repeat, Plus, ExternalLink, ChevronDown, ChevronRight, ChevronLeft, Eye, Store, Sparkles, Star, Trash2, RefreshCw, GripVertical, Pencil } from 'lucide-react';
import { SortablePhotoGrid } from '../common/SortablePhotoGrid';
import PhotoEditModal, { buildPhotoSummary } from '../common/PhotoEditModal';
import { AppIcons } from '../../constants/icons';
import { CategoryColors, PriorityColors, type PriorityKey } from '../../constants/categoryColors';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { getCurrentUser } from '../../firebase/auth';
import { buildDestashFields, clearDestashFields } from '../../services/destashDenormalization';
import { uploadPhoto, deletePhoto } from '../../services/photoUploadService';
import StorageUpgradeModal from '../StorageUpgradeModal';
import ImageCarousel from '../ImageCarousel';
import TagSelector from '../TagSelector';
import ConditionSelector from '../common/ConditionSelector';
import BrandAutocomplete from '../common/BrandAutocomplete';
import CustomTagsEditor from '../common/CustomTagsEditor';
import PrivateLockBadge from '../common/PrivateLockBadge';
import { useSellerCurrency } from '../../hooks/useSellerCurrency';
import { usePinnedCurrencies } from '../../hooks/usePinnedCurrencies';
import { formatPrice, getCurrencyInfo } from '../../constants/currencies';
import CurrencyPicker from '../common/CurrencyPicker';
import type { CollectionItem, Photo } from '../../types';
import type { BrandCategory } from '../../types/brandCategories';
import { ACCEPTED_IMAGE_TYPES } from '../../utils/imageUtils';
import { normalizeTypography } from '../../utils/normalizeTypography';
import type {
  BaseType,
  Finish,
  HoloType,
  ColorShift,
  Particle,
  SpecialFeature,
  ColorFamily,
  Utility,
  ColorCharacter
} from '../../types';

interface PolishDetailModalProps {
  polish: CollectionItem;
  onClose: () => void;
  onUpdate?: (polishId: string, updates: Partial<CollectionItem>) => void;
  onDelete?: (polishId: string) => void;
  onRestore?: (polishId: string) => void;
  onPermanentDelete?: (polishId: string) => void;
  isInTrash?: boolean;
  onNext?: () => void;
  onPrev?: () => void;
  hasNext?: boolean;
  hasPrev?: boolean;
  isPublicView?: boolean; // True when viewing someone else's item
  existingBrands?: string[]; // Brands already in user's collection for autocomplete
  userBrandCategories?: Record<string, BrandCategory>; // User's custom brand categorizations
}

export default function PolishDetailModal({ polish, onClose, onUpdate, onDelete, onRestore, onPermanentDelete, isInTrash = false, onNext, onPrev, hasNext, hasPrev, isPublicView = false, existingBrands = [], userBrandCategories = {} }: PolishDetailModalProps) {
  // Helper: safely convert any date-like value (Date, Firestore Timestamp, string) to a JS Date
  const parseDate = (value: any): Date | null => {
    if (!value) return null;
    // Firestore Timestamp objects have a toDate() method
    if (typeof value?.toDate === 'function') return value.toDate();
    // Already a Date
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
    // ISO string or other string
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  };
  // Toggle states
  const [onWishlist, setOnWishlist] = useState(polish.onWishlist || false);
  const [isoPriority, setIsoPriority] = useState<'iso' | 'diso' | 'unicorn'>(polish.isoPriority || 'iso');
  const [needsInfo, setNeedsInfo] = useState(polish.needsInfo || false);
  const [isFavorite, setIsFavorite] = useState(polish.isFavorite || false);
  const [isRareLE, setIsRareLE] = useState(polish.isRareLE || false);
  const [swatched, setSwatched] = useState(polish.swatched || false);
  // Private polish — hides from all public surfaces. Marketplace is
  // intentionally unaffected; if isForDestash is also on, buyers still
  // see this on Marketplace and we warn the user about it inline.
  const [isPrivate, setIsPrivate] = useState(polish.isPrivate || false);

  // Seller's chosen currency from profile. Drives the symbol shown in the
  // DESTASH price input and the fallback for legacy items that don't have
  // their own priceCurrency stamped yet.
  const currency = useSellerCurrency();
  const { pinnedCurrencies, setPinnedCurrencies } = usePinnedCurrencies();
  // Per-item currency for the OWNER's Purchase Price (record-keeping).
  // Distinct from `currency` above.
  const [purchaseCurrency, setPurchaseCurrency] = useState<string>(polish.priceCurrency || '');
  const purchaseSymbol = purchaseCurrency
    ? getCurrencyInfo(purchaseCurrency).symbol
    : currency.symbol;
  const purchaseSymbolPad =
    purchaseSymbol.length <= 1 ? 'pl-7' : purchaseSymbol.length <= 2 ? 'pl-10' : 'pl-12';
  const symbolPad =
    currency.symbol.length <= 1 ? 'pl-7' : currency.symbol.length <= 2 ? 'pl-10' : 'pl-12';

  // Marketplace / Destash listing state
  const [isForDestash, setIsForDestash] = useState(polish.isForDestash || false);
  const [destashCondition, setDestashCondition] = useState(polish.destashCondition || '');
  const [destashPrice, setDestashPrice] = useState(polish.destashPrice?.toString() || '');
  const [isOpenToSwaps, setIsOpenToSwaps] = useState(polish.isOpenToSwaps || false);
  const [destashNotes, setDestashNotes] = useState(polish.destashNotes || '');
  const [destashQuantityAvailable, setDestashQuantityAvailable] = useState(polish.destashQuantityAvailable || polish.quantity || 1);
  const [destashSaved, setDestashSaved] = useState(false);
  const [showListedSuccess, setShowListedSuccess] = useState(false);
  const [wasAlreadyListed] = useState(polish.isForDestash || false); // Track if already listed when opened

  // Archive/Destash completion modals (for when item actually leaves collection)
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [archiveMethod, setArchiveMethod] = useState<string>('sold');
  const [archiveSalePrice, setArchiveSalePrice] = useState<string>('');
  const [archiveNotes, setArchiveNotes] = useState<string>('');
  const [archiveKeepQuantity, setArchiveKeepQuantity] = useState<number>(0); // How many to keep when archiving multiples
  const [_archiveQuantityToRemove, setArchiveQuantityToRemove] = useState<number>(1); // How many leaving collection

  // Usage tracking
  const [timesUsed, setTimesUsed] = useState(polish.timesUsed || 0);
  const [lastUsedDate, setLastUsedDate] = useState(polish.lastUsedDate);
  const [usageSaved, setUsageSaved] = useState(false);
  const [isEditingUsage, setIsEditingUsage] = useState(false);

  // Edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [_editSaved, setEditSaved] = useState(false);

  // Editable fields
  const [brand, setBrand] = useState(polish.brand || '');
  const [name, setName] = useState(polish.name || '');
  const [colorDescription, setColorDescription] = useState(polish.colorDescription || '');
  const [category, setCategory] = useState(polish.category || '');
  const [storage_, setStorage_] = useState(polish.storage || '');
  const [barcode, setBarcode] = useState(polish.barcode || '');
  const [line_, setLine_] = useState(polish.line || '');
  const [swatchNumber, setSwatchNumber] = useState(polish.swatchNumber || '');
  const [price, setPrice] = useState(polish.price?.toString() || '');
  // Purchase date lives on `polish.purchaseDate` as a Date | Firestore
  // Timestamp | ISO string depending on origin. Normalize to a YYYY-MM-DD
  // string for the <input type="date"> control — empty string means unset.
  const [purchaseDate, setPurchaseDate] = useState(() => {
    const v: any = polish.purchaseDate;
    if (!v) return '';
    try {
      const d =
        v instanceof Date
          ? v
          : typeof v?.toDate === 'function'
            ? v.toDate()
            : new Date(v);
      if (isNaN(d.getTime())) return '';
      return d.toISOString().slice(0, 10);
    } catch {
      return '';
    }
  });
  const [quantity, setQuantity] = useState(polish.quantity?.toString() || '1');

  // NEW: Release system state - RESTRUCTURED!
  type ReleaseType = 'core' | 'collection' | 'event' | 'box' | 'groupCustom' | 'exclusive' | 'charity' | 'limitedEdition' | 'convention' | 'other' | 'unknown';
  const [releaseType, setReleaseType] = useState<ReleaseType>((polish.releaseType as ReleaseType) || 'unknown');
  
  // Conditional fields based on releaseType
  const [collectionName, setCollectionName] = useState(polish.collectionName || '');
  const [eventName, setEventName] = useState(polish.eventName || polish.eventSource || ''); // Migrate from eventSource
  const [eventTheme, setEventTheme] = useState(polish.eventTheme || '');
  const [boxName, setBoxName] = useState(polish.boxName || polish.box || ''); // Migrate from box
  const [boxTheme, setBoxTheme] = useState(polish.boxTheme || '');
  const [groupName, setGroupName] = useState(polish.groupName || '');
  const [groupTheme, setGroupTheme] = useState(polish.groupTheme || '');
  const [exclusiveTo, setExclusiveTo] = useState(polish.exclusiveTo || '');
  const [exclusiveTheme, setExclusiveTheme] = useState(polish.exclusiveTheme || '');
  const [charityName, setCharityName] = useState(polish.charityName || '');
  const [charityCause, setCharityCause] = useState(polish.charityCause || '');
  const [conventionName, setConventionName] = useState(polish.conventionName || '');
  
  // Always available fields
  const [releaseYear, setReleaseYear] = useState(polish.releaseYear?.toString() || '');
  const [releaseSeason, setReleaseSeason] = useState(polish.releaseSeason || '');
  const [releaseMonth, setReleaseMonth] = useState(polish.releaseMonth?.toString() || '');
  const [collabWith, setCollabWith] = useState(polish.collabWith || polish.collab || ''); // Migrate from collab
  const [collabTheme, setCollabTheme] = useState(polish.collabTheme || '');
  
  // Re-release tracking
  const [isRerelease, setIsRerelease] = useState(polish.isRerelease || false);
  const [originalReleaseYear, setOriginalReleaseYear] = useState(polish.originalReleaseYear?.toString() || '');
  const [originalReleaseSeason, setOriginalReleaseSeason] = useState(polish.originalReleaseSeason || '');
  const [releaseNotes, setReleaseNotes] = useState(polish.releaseNotes || '');
  
  // Duo/Trio/Set tracking
  const [setNameField, setSetNameField] = useState(polish.setName || '');
  const [setType, setSetType] = useState<'duo' | 'trio' | 'quad' | 'set' | ''>(polish.setType || '');

  // Notes
  const [publicNotes, setPublicNotes] = useState(polish.publicNotes || '');
  const [privateNotes, setPrivateNotes] = useState(polish.notes || '');
  const [notesSaved, setNotesSaved] = useState(false);
  const [toggleSaved, setToggleSaved] = useState(false);

  // Structured tags
  const [baseType, setBaseType] = useState<BaseType | undefined>(polish.baseType);
  const [finish, setFinish] = useState<Finish[]>(polish.finish || []);
  const [holoType, setHoloType] = useState<HoloType[]>(polish.holoType || []);
  const [colorShift, setColorShift] = useState<ColorShift | undefined>(polish.colorShift);
  const [particles, setParticles] = useState<Particle[]>(polish.particles || []);
  const [specialFeatures, setSpecialFeatures] = useState<SpecialFeature[]>(polish.specialFeatures || []);
  const [colorFamily, setColorFamily] = useState<ColorFamily[]>(polish.colorFamily || []);
  const [utility, setUtility] = useState<Utility[]>(polish.utility || []);
  const [colorCharacter, setColorCharacter] = useState<ColorCharacter[]>(polish.colorCharacter || []);
  /** Freeform tags[] array — the value we now surface in the modal via
   *  CustomTagsEditor. Seeded from the doc's `tags` (falls back to []
   *  for items that don't have any). `polish` may not have `tags` on
   *  its type, so we read defensively.
   *  Values that also live in a structured field (e.g. `baseType: 'sheer'`
   *  plus `tags: [..., 'sheer']`, typical of CSV imports) are stripped
   *  here — otherwise removing the tag from the Custom Tags editor looks
   *  broken because the chip re-appears from `baseType`. */
  const [customTags, setCustomTags] = useState<string[]>(() => {
    const raw = Array.isArray((polish as { tags?: unknown }).tags)
      ? ((polish as { tags: unknown[] }).tags.filter(
          (t): t is string => typeof t === 'string'
        ) as string[])
      : [];
    const structured = new Set<string>(
      [
        polish.baseType ? [polish.baseType] : [],
        polish.finish ?? [],
        polish.holoType ?? [],
        polish.particles ?? [],
        polish.specialFeatures ?? [],
        polish.colorFamily ?? [],
        polish.utility ?? [],
        polish.colorCharacter ?? [],
      ]
        .flat()
        .map((v) => (typeof v === 'string' ? v.trim().toLowerCase() : ''))
        .filter(Boolean),
    );
    return raw.filter((t) => !structured.has(t.trim().toLowerCase()));
  });
  const [customTagsSaved, setCustomTagsSaved] = useState(false);
  const [tagsSaved, setTagsSaved] = useState(false);

  // Photo management
  const [photos, setPhotos] = useState<Photo[]>(polish.photos || []);
  const [editingPhotoIdx, setEditingPhotoIdx] = useState<number | null>(null);
  const [isEditingPhotos, setIsEditingPhotos] = useState(false);
  const [_photosSaved, setPhotosSaved] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [showStorageUpgradeModal, setShowStorageUpgradeModal] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Collapsible sections - auto-expand marketplace if already listed
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    if (polish.isForDestash) {
      initial.add('marketplace');
    }
    return initial;
  });

  // Public view - Add to ISO/PublicWishlist
  const [showIsoMenu, setShowIsoMenu] = useState(false);
  const [addedToIso, setAddedToIso] = useState(false);
  const [isoLevel, setIsoLevel] = useState<'iso' | 'diso' | 'unicorn' | null>(null);

  // Toggle section expansion
  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  // Sync state when polish prop changes
  useEffect(() => {
    setNeedsInfo(polish.needsInfo || false);
    setOnWishlist(polish.onWishlist || false);
    setIsoPriority(polish.isoPriority || 'iso');
    setIsFavorite(polish.isFavorite || false);
    setIsRareLE(polish.isRareLE || false);
    setSwatched(polish.swatched || false);
    setIsPrivate(polish.isPrivate || false);
    setTimesUsed(polish.timesUsed || 0);
    setLastUsedDate(polish.lastUsedDate);

    // Sync destash listing fields
    setIsForDestash(polish.isForDestash || false);
    setDestashCondition(polish.destashCondition || '');
    setDestashPrice(polish.destashPrice?.toString() || '');
    setIsOpenToSwaps(polish.isOpenToSwaps || false);
    setDestashNotes(polish.destashNotes || '');
    setDestashQuantityAvailable(polish.destashQuantityAvailable || polish.quantity || 1);

    // Sync structured tags
    setBaseType(polish.baseType);
    setFinish(polish.finish || []);
    setHoloType(polish.holoType || []);
    setColorShift(polish.colorShift);
    setParticles(polish.particles || []);
    setSpecialFeatures(polish.specialFeatures || []);
    setColorFamily(polish.colorFamily || []);
    setUtility(polish.utility || []);
    setColorCharacter(polish.colorCharacter || []);
  }, [polish]);

  // Close ISO menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (showIsoMenu) {
        setShowIsoMenu(false);
      }
    };
    if (showIsoMenu) {
      // Add slight delay to avoid immediate close
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 100);
    }
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showIsoMenu]);

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Track unsaved changes
  useEffect(() => {
    if (!isEditing) {
      setHasUnsavedChanges(false);
      return;
    }
    const hasChanges = 
      brand !== (polish.brand || '') ||
      name !== (polish.name || '') ||
      colorDescription !== (polish.colorDescription || '') ||
      category !== (polish.category || '') ||
      storage_ !== (polish.storage || '') ||
      barcode !== (polish.barcode || '') ||
      line_ !== (polish.line || '') ||
      swatchNumber !== (polish.swatchNumber || '') ||
      price !== (polish.price?.toString() || '') ||
      purchaseCurrency !== (polish.priceCurrency || '') ||
      purchaseDate !== (() => {
        const v: any = polish.purchaseDate;
        if (!v) return '';
        try {
          const d = v instanceof Date ? v : typeof v?.toDate === 'function' ? v.toDate() : new Date(v);
          return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
        } catch { return ''; }
      })() ||
      quantity !== (polish.quantity?.toString() || '1') ||
      // Release fields - NEW STRUCTURE
      releaseType !== (polish.releaseType || 'unknown') ||
      collectionName !== (polish.collectionName || '') ||
      eventName !== (polish.eventName || polish.eventSource || '') ||
      eventTheme !== (polish.eventTheme || '') ||
      boxName !== (polish.boxName || polish.box || '') ||
      boxTheme !== (polish.boxTheme || '') ||
      groupName !== (polish.groupName || '') ||
      groupTheme !== (polish.groupTheme || '') ||
      exclusiveTo !== (polish.exclusiveTo || '') ||
      exclusiveTheme !== (polish.exclusiveTheme || '') ||
      charityName !== (polish.charityName || '') ||
      charityCause !== (polish.charityCause || '') ||
      conventionName !== (polish.conventionName || '') ||
      releaseYear !== (polish.releaseYear?.toString() || '') ||
      releaseSeason !== (polish.releaseSeason || '') ||
      releaseMonth !== (polish.releaseMonth?.toString() || '') ||
      collabWith !== (polish.collabWith || polish.collab || '') ||
      collabTheme !== (polish.collabTheme || '') ||
      isRerelease !== (polish.isRerelease || false) ||
      originalReleaseYear !== (polish.originalReleaseYear?.toString() || '') ||
      originalReleaseSeason !== (polish.originalReleaseSeason || '') ||
      releaseNotes !== (polish.releaseNotes || '') ||
      setNameField !== (polish.setName || '') ||
      setType !== (polish.setType || '') ||
      isPrivate !== (polish.isPrivate || false);
    setHasUnsavedChanges(hasChanges);
  }, [isEditing, brand, name, colorDescription, category, storage_, barcode, line_, swatchNumber, price, purchaseCurrency, purchaseDate, quantity, releaseType, collectionName, eventName, eventTheme, boxName, boxTheme, groupName, groupTheme, exclusiveTo, exclusiveTheme, charityName, charityCause, conventionName, releaseYear, releaseSeason, releaseMonth, collabWith, collabTheme, isRerelease, originalReleaseYear, originalReleaseSeason, releaseNotes, setNameField, setType, isPrivate, polish]);

  // Toggle handler (for simple toggles only)
  const handleToggle = async (field: string, value: boolean) => {
    switch (field) {
      case 'onWishlist': 
        setOnWishlist(value);
        // When enabling wishlist, set default ISO priority
        if (value && !polish.isoPriority) {
          setIsoPriority('iso');
        }
        break;
      case 'needsInfo': setNeedsInfo(value); break;
      case 'isFavorite': setIsFavorite(value); break;
      case 'isRareLE': setIsRareLE(value); break;
      case 'swatched': setSwatched(value); break;
      case 'isPrivate': setIsPrivate(value); break;
    }
    if (onUpdate) {
      const updates: any = { [field]: value };
      // When enabling wishlist, also save the default ISO priority
      if (field === 'onWishlist' && value) {
        updates.isoPriority = polish.isoPriority || 'iso';
      }
      await onUpdate(polish.id, updates);
      setToggleSaved(true);
      setTimeout(() => setToggleSaved(false), 1500);
    }
  };

  // Handle ISO priority change
  const handleIsoPriorityChange = async (priority: 'iso' | 'diso' | 'unicorn') => {
    setIsoPriority(priority);
    if (onUpdate) {
      await onUpdate(polish.id, { isoPriority: priority });
      setToggleSaved(true);
      setTimeout(() => setToggleSaved(false), 1500);
    }
  };

  // Handle adding to own wishlist when viewing someone else's item (public view)
  const handleAddToMyWishlist = async (priority: 'iso' | 'diso' | 'unicorn') => {
    const currentUser = getCurrentUser();
    if (!currentUser) return;

    try {
      // Add to the current user's own collection as a wishlist item
      const itemsRef = collection(db, 'users', currentUser.uid, 'items');
      
      // Copy first photo WITH source tracking for DMCA
      const trackedPhotos = polish.photos?.slice(0, 1).map(photo => ({
        ...photo,
        // DMCA Source Tracking
        uploadedBy: currentUser.uid,
        uploadedAt: new Date().toISOString(),
        source: 'web' as const,
        sourceUrl: photo.url,
        sourceUserId: polish.userId || undefined,
        dmcaStatus: 'active' as const,
        ...(photo.creditName && { creditName: photo.creditName }),
        ...(photo.creditUrl && { creditUrl: photo.creditUrl }),
      })) || [];

      // Normalize smart-quote / typographic-dash variants down to plain
      // ASCII when copying so the copied item lands in Firestore in the
      // canonical form even if the source item predates normalization.
      const normalizedBrand = normalizeTypography((polish.brand ?? '').trim());
      const normalizedName = normalizeTypography((polish.name ?? '').trim());
      await addDoc(itemsRef, {
        // Copy basic info from the item they're viewing
        brand: normalizedBrand,
        brandLower: normalizedBrand.toLowerCase(),
        name: normalizedName,
        nameLower: normalizedName.toLowerCase(),
        category: polish.category || 'Nail Lacquer',
        itemType: 'polish',
        colorDescription: polish.colorDescription || '',
        // Set as wishlist item
        onWishlist: true,
        isoPriority: priority,
        quantity: 0, // They don't own it yet
        // Copy photos with tracking
        photos: trackedPhotos,
        // Copy tags
        baseType: polish.baseType,
        finish: polish.finish,
        holoType: polish.holoType,
        colorShift: polish.colorShift,
        particles: polish.particles,
        specialFeatures: polish.specialFeatures,
        colorFamily: polish.colorFamily,
        utility: polish.utility,
        colorCharacter: polish.colorCharacter,
        // Add note about source
        publicNotes: `Spotted in ${polish.userId ? "another collector's" : "a"} collection`,
        // Metadata
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setAddedToIso(true);
      setIsoLevel(priority);
      setShowIsoMenu(false);
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      alert('Failed to add to your wishlist. Please try again.');
    }
  };

  // Destash listing handler - saves all destash fields together
  const handleSaveDestashListing = async () => {
    if (!onUpdate) return;

    if (isForDestash) {
      // Validate required fields
      if (!destashCondition) {
        alert('Please select a condition to list on the marketplace');
        return;
      }
      if (!destashPrice) {
        alert('Please enter a price to list on the marketplace');
        return;
      }

      // Snapshot the seller's currency + ships-to onto the listing so
      // Marketplace queries can filter without joining to the user doc.
      const currentUser = getCurrentUser();
      const denorm = currentUser
        ? await buildDestashFields(currentUser.uid)
        : clearDestashFields();
      await onUpdate(polish.id, {
        isForDestash: true,
        destashCondition: destashCondition as 'New' | 'Used - Light Use' | 'Swatched Only' | 'Used - Moderate',
        destashPrice: parseFloat(destashPrice),
        destashQuantityAvailable,
        isOpenToSwaps,
        destashNotes: destashNotes || undefined,
        destashStatus: 'available',
        destashDate: (polish.destashDate || new Date()) as Date,
        ...denorm,
      });

      // Show success banner (persists until modal closes)
      setShowListedSuccess(true);
    } else {
      // Turning off destash - clear marketplace fields
      await onUpdate(polish.id, {
        isForDestash: false,
        destashCondition: undefined,
        destashPrice: undefined,
        destashQuantityAvailable: undefined,
        isOpenToSwaps: false,
        destashNotes: undefined,
        destashStatus: undefined,
        ...clearDestashFields(),
      });
    }

    setDestashSaved(true);
    setTimeout(() => setDestashSaved(false), 2000);
  };

  // Toggle destash listing on/off
  const handleToggleDestash = (enabled: boolean) => {
    setIsForDestash(enabled);
    if (enabled) {
      // Set default quantity to total quantity when enabling
      setDestashQuantityAvailable(parseInt(quantity) || 1);
    } else {
      // Clear fields when turning off
      setDestashCondition('');
      setDestashPrice('');
      setIsOpenToSwaps(false);
      setDestashNotes('');
      setDestashQuantityAvailable(parseInt(quantity) || 1);
      // Auto-save the removal
      if (onUpdate) {
        onUpdate(polish.id, {
          isForDestash: false,
          destashCondition: undefined,
          destashPrice: undefined,
          destashQuantityAvailable: undefined,
          isOpenToSwaps: false,
          destashNotes: undefined,
          destashStatus: undefined,
          ...clearDestashFields(),
        });
        setDestashSaved(true);
        setTimeout(() => setDestashSaved(false), 2000);
      }
    }
  };

  // Remove listing and close modal
  const handleRemoveListingAndClose = async () => {
    if (onUpdate) {
      await onUpdate(polish.id, {
        isForDestash: false,
        destashCondition: undefined,
        destashPrice: undefined,
        destashQuantityAvailable: undefined,
        isOpenToSwaps: false,
        destashNotes: undefined,
        destashStatus: undefined,
        destashDate: undefined,
        ...clearDestashFields(),
      });
    }
    onClose();
  };

  // Tag change handler
  const handleTagChange = async (categoryKey: string, value: any) => {
    switch (categoryKey) {
      case 'baseType': setBaseType(value); break;
      case 'finish': setFinish(value); break;
      case 'holoType': setHoloType(value); break;
      case 'colorShift': setColorShift(value); break;
      case 'particles': setParticles(value); break;
      case 'specialFeatures': setSpecialFeatures(value); break;
      case 'colorFamily': setColorFamily(value); break;
      case 'utility': setUtility(value); break;
      case 'colorCharacter': setColorCharacter(value); break;
    }
    if (onUpdate) {
      await onUpdate(polish.id, { [categoryKey]: value });
      setTagsSaved(true);
      setTimeout(() => setTagsSaved(false), 1500);
    }
  };

  /** Custom tags change handler — persists tags[] on every mutation so
   *  the section behaves like the structured Tags section (auto-save
   *  on change; no explicit "save" button). Kept separate from
   *  handleTagChange because the structured tags use per-category
   *  writes and this one always writes the full array. */
  const handleCustomTagsChange = async (next: string[]) => {
    setCustomTags(next);
    if (onUpdate) {
      await onUpdate(polish.id, { tags: next });
      setCustomTagsSaved(true);
      setTimeout(() => setCustomTagsSaved(false), 1500);
    }
  };

  // Usage tracking
  const handleLogUsage = async () => {
    const newTimesUsed = timesUsed + 1;
    const newLastUsedDate = new Date();
    setTimesUsed(newTimesUsed);
    setLastUsedDate(newLastUsedDate);
    if (onUpdate) {
      await onUpdate(polish.id, { timesUsed: newTimesUsed, lastUsedDate: newLastUsedDate });
      setUsageSaved(true);
      setTimeout(() => setUsageSaved(false), 1500);
    }
  };

  // Save edited usage values
  const handleSaveUsageEdit = async () => {
    if (onUpdate) {
      // Keep the counter and the date coherent — a saved Last Used date
      // with a count of 0 renders as "0 times used" and looks broken. If
      // the user set a date but left the count at 0, promote it to 1.
      const coercedTimesUsed =
        lastUsedDate && (timesUsed == null || timesUsed <= 0) ? 1 : timesUsed;
      const updates: any = { timesUsed: coercedTimesUsed };
      if (lastUsedDate) {
        // Always save as ISO string for consistent Firestore handling
        const parsed = parseDate(lastUsedDate);
        updates.lastUsedDate = parsed ? parsed.toISOString() : null;
      } else {
        updates.lastUsedDate = null;
      }
      await onUpdate(polish.id, updates);
      setUsageSaved(true);
      setIsEditingUsage(false);
      setTimeout(() => setUsageSaved(false), 1500);
    }
  };

  // Edit mode handlers
  const handleEdit = () => {
    setBrand(polish.brand || '');
    setName(polish.name || '');
    setColorDescription(polish.colorDescription || '');
    setCategory(polish.category || '');
    setStorage_(polish.storage || '');
    setBarcode(polish.barcode || '');
    setLine_(polish.line || '');
    setSwatchNumber(polish.swatchNumber || '');
    setPrice(polish.price?.toString() || '');
    setPurchaseCurrency(polish.priceCurrency || '');
    setPurchaseDate(() => {
      const v: any = polish.purchaseDate;
      if (!v) return '';
      try {
        const d = v instanceof Date ? v : typeof v?.toDate === 'function' ? v.toDate() : new Date(v);
        return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
      } catch { return ''; }
    });
    setQuantity(polish.quantity?.toString() || '1');
    // Release fields - NEW STRUCTURE
    setReleaseType((polish.releaseType as ReleaseType) || 'unknown');
    setCollectionName(polish.collectionName || '');
    setEventName(polish.eventName || polish.eventSource || ''); // Migrate from eventSource
    setEventTheme(polish.eventTheme || '');
    setBoxName(polish.boxName || polish.box || ''); // Migrate from box
    setBoxTheme(polish.boxTheme || '');
    setGroupName(polish.groupName || '');
    setGroupTheme(polish.groupTheme || '');
    setExclusiveTo(polish.exclusiveTo || '');
    setExclusiveTheme(polish.exclusiveTheme || '');
    setCharityName(polish.charityName || '');
    setCharityCause(polish.charityCause || '');
    setConventionName(polish.conventionName || '');
    setReleaseYear(polish.releaseYear?.toString() || '');
    setReleaseSeason(polish.releaseSeason || '');
    setReleaseMonth(polish.releaseMonth?.toString() || '');
    setCollabWith(polish.collabWith || polish.collab || ''); // Migrate from collab
    setCollabTheme(polish.collabTheme || '');
    setIsRerelease(polish.isRerelease || false);
    setOriginalReleaseYear(polish.originalReleaseYear?.toString() || '');
    setOriginalReleaseSeason(polish.originalReleaseSeason || '');
    setReleaseNotes(polish.releaseNotes || '');
    setSetNameField(polish.setName || '');
    setSetType(polish.setType || '');
    setIsPrivate(polish.isPrivate || false);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setHasUnsavedChanges(false);
  };

  const handleSaveEdit = async () => {
    if (onUpdate) {
      // Normalize smart-quote / typographic-dash variants down to plain
      // ASCII on rename so an iOS user renaming a polish writes the same
      // string as an Android user. Prevents typography-only duplicates.
      const normalizedBrand = normalizeTypography(brand.trim());
      const normalizedName = normalizeTypography(name.trim());
      // Build updates object, filtering out undefined values (Firestore doesn't accept undefined)
      const updates: Partial<CollectionItem> = {
        brand: normalizedBrand,
        name: normalizedName,
        colorDescription,
        category: category as typeof polish.category,
        storage: storage_,
        barcode,
        line: line_ || null as any,
        swatchNumber: swatchNumber || null as any,
        quantity: parseInt(quantity) || 1,
        releaseType: (releaseType || 'unknown') as typeof polish.releaseType,
      };
      
      // Only include optional fields if they have values (use null to clear, not undefined)
      if (price) {
        updates.price = parseFloat(price);
      } else if (polish.price !== undefined) {
        // Clear the field if it was previously set
        updates.price = null as any;
      }

      // Per-item purchase currency. Only stamp when there's a price to
      // pair it with; clearing price also clears currency to avoid an
      // orphan currency field on an item with no amount.
      if (price && purchaseCurrency) {
        updates.priceCurrency = purchaseCurrency;
      } else if (polish.priceCurrency !== undefined) {
        updates.priceCurrency = null as any;
      }

      if (purchaseDate) {
        // Store as Date so it matches the CollectionItem.purchaseDate type
        // and the Add-modal write shape (both use `new Date(str)`).
        updates.purchaseDate = new Date(purchaseDate) as any;
      } else if (polish.purchaseDate !== undefined) {
        updates.purchaseDate = null as any;
      }
      
      // ============================================
      // NEW RELEASE STRUCTURE - Conditional fields
      // ============================================
      
      // Clear old fields that are being replaced
      updates.eventSource = null as any; // Replaced by eventName
      updates.box = null as any; // Replaced by boxName
      
      // Conditional fields based on releaseType
      if (releaseType === 'collection') {
        updates.collectionName = collectionName || '';
      } else {
        updates.collectionName = '';
      }
      
      if (releaseType === 'event') {
        (updates as any).eventName = eventName || '';
        updates.eventTheme = eventTheme || '';
      } else {
        (updates as any).eventName = '';
        updates.eventTheme = '';
      }
      
      if (releaseType === 'box') {
        (updates as any).boxName = boxName || '';
        (updates as any).boxTheme = boxTheme || '';
      } else {
        (updates as any).boxName = '';
        (updates as any).boxTheme = '';
      }
      
      if (releaseType === 'groupCustom') {
        (updates as any).groupName = groupName || '';
        (updates as any).groupTheme = groupTheme || '';
      } else {
        (updates as any).groupName = '';
        (updates as any).groupTheme = '';
      }
      
      if (releaseType === 'exclusive') {
        (updates as any).exclusiveTo = exclusiveTo || '';
        (updates as any).exclusiveTheme = exclusiveTheme || '';
      } else {
        (updates as any).exclusiveTo = '';
        (updates as any).exclusiveTheme = '';
      }
      
      if (releaseType === 'charity') {
        (updates as any).charityName = charityName || '';
        (updates as any).charityCause = charityCause || '';
      } else {
        (updates as any).charityName = '';
        (updates as any).charityCause = '';
      }
      
      if (releaseType === 'convention') {
        (updates as any).conventionName = conventionName || '';
      } else {
        (updates as any).conventionName = '';
      }
      
      // Always available fields
      updates.releaseSeason = (releaseSeason || '') as typeof polish.releaseSeason;
      // Release month - always include (use null to clear)
      (updates as any).releaseMonth = releaseMonth ? parseInt(releaseMonth) : null;
      (updates as any).collabWith = collabWith || '';
      (updates as any).collabTheme = collabTheme || '';
      
      // Re-release tracking
      (updates as any).isRerelease = isRerelease;
      (updates as any).originalReleaseSeason = isRerelease ? (originalReleaseSeason || '') : '';
      (updates as any).releaseNotes = releaseNotes || '';
      if (isRerelease && originalReleaseYear) {
        (updates as any).originalReleaseYear = parseInt(originalReleaseYear);
      } else {
        (updates as any).originalReleaseYear = null;
      }
      
      // Duo/Trio/Set tracking
      (updates as any).setType = setType || null;
      (updates as any).setName = setType ? (setNameField || '') : '';

      // Privacy flag — write unconditionally so toggling OFF also persists.
      (updates as any).isPrivate = isPrivate;
      
      // Number field - use null to clear
      if (releaseYear) {
        updates.releaseYear = parseInt(releaseYear);
      } else if (polish.releaseYear !== undefined) {
        updates.releaseYear = null as any;
      }
      
      await onUpdate(polish.id, updates);
      setEditSaved(true);
      setIsEditing(false);
      setHasUnsavedChanges(false);
      setTimeout(() => setEditSaved(false), 1500);
    }
  };

  // Notes handler
  const handleSaveNotes = async () => {
    if (onUpdate) {
      await onUpdate(polish.id, { publicNotes, notes: privateNotes });
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 1500);
    }
  };

  // Archive handler (item actually left collection - sold, traded, etc.)
  const handleArchive = async () => {
    if (onUpdate) {
      const currentQty = polish.quantity || 1;
      
      if (archiveKeepQuantity > 0 && archiveKeepQuantity < currentQty) {
        // Partial archive - keeping some, reducing quantity
        await onUpdate(polish.id, {
          quantity: archiveKeepQuantity,
          // Clear marketplace listing if it was listed
          isForDestash: false,
          destashCondition: undefined,
          destashPrice: undefined,
          isOpenToSwaps: false,
          destashStatus: undefined,
          ...clearDestashFields(),
        });
        setShowArchiveModal(false);
        // Reset archive form
        setArchiveKeepQuantity(0);
        setArchiveQuantityToRemove(1);
        setArchiveMethod('sold');
        setArchiveSalePrice('');
        setArchiveNotes('');
        // Don't close main modal - they still have some!
      } else {
        // Full archive - keeping 0, item completely leaves collection
        await onUpdate(polish.id, {
          isDestashed: true,
          destashDate: new Date(),
          destashMethod: archiveMethod as any,
          destashSalePrice: archiveSalePrice ? parseFloat(archiveSalePrice) : undefined,
          destashNotes: archiveNotes || undefined,
          isForDestash: false, // Remove from "for destash" list since it's now archived
          destashCondition: undefined,
          destashPrice: undefined,
          isOpenToSwaps: false,
          destashStatus: undefined,
          quantity: 0,
          ...clearDestashFields(),
        });
        setShowArchiveModal(false);
        onClose(); // Close the main modal
      }
    }
  };

  // Un-archive handler (bring back to collection)
  const handleUnArchive = async () => {
    if (onUpdate) {
      await onUpdate(polish.id, {
        isDestashed: false,
        destashDate: undefined,
        destashMethod: undefined,
        destashSalePrice: undefined,
      });
    }
  };

  // Delete handler (soft delete - moves to trash)
  const handleDelete = async () => {
    if (onDelete) {
      await onDelete(polish.id);
      setShowDeleteConfirm(false);
      onClose();
    }
  };

  // Restore handler (restore from trash)
  const handleRestore = async () => {
    if (onRestore) {
      await onRestore(polish.id);
      onClose();
    }
  };

  // Permanent delete handler (actually removes from database)
  const [showPermanentDeleteConfirm, setShowPermanentDeleteConfirm] = useState(false);
  const handlePermanentDelete = async () => {
    if (onPermanentDelete) {
      await onPermanentDelete(polish.id);
      setShowPermanentDeleteConfirm(false);
      onClose();
    }
  };

  // Photo handlers
  /** Build the persist-shape for a Photo — trim optional strings, preserve
   *  photoRightsAttested (a one-shot rights toggle that must NOT be dropped
   *  when other photo actions save). */
  const cleanPhotoForSave = (photo: Photo): Photo => {
    const cleaned: Photo = { url: photo.url, isDefault: photo.isDefault };
    if (photo.caption?.trim()) cleaned.caption = photo.caption.trim();
    if (photo.creditName?.trim()) cleaned.creditName = photo.creditName.trim();
    if (photo.creditUrl?.trim()) cleaned.creditUrl = photo.creditUrl.trim();
    if (photo.photoRightsAttested === true) cleaned.photoRightsAttested = true;
    return cleaned;
  };

  const updatePhoto = async (index: number, updates: Partial<Photo>) => {
    const nextPhotos = photos.map((photo, i) => i === index ? { ...photo, ...updates } : photo);
    setPhotos(nextPhotos);
    // Persist immediately if the change includes photoRightsAttested — the
    // PerPhotoReAttest checkbox is a one-shot attestation that should stick
    // even if the user closes the modal without another action.
    if ('photoRightsAttested' in updates && onUpdate) {
      const cleaned = nextPhotos.map(cleanPhotoForSave);
      try {
        console.log('[attest] saving photos:', cleaned);
        await onUpdate(polish.id, { photos: cleaned });
        console.log('[attest] save complete');
      } catch (err) {
        console.error('[attest] save failed:', err);
        // Revert local state so the checkbox doesn't lie about being saved.
        setPhotos(photos);
        alert('Could not save photo rights attestation. Please try again or check your connection.');
      }
    }
  };

  const setDefaultPhoto = async (index: number) => {
    const updatedPhotos = photos.map((photo, i) => ({ ...photo, isDefault: i === index }));
    setPhotos(updatedPhotos);
    if (onUpdate) {
      const cleanedPhotos = updatedPhotos.map(cleanPhotoForSave);
      await onUpdate(polish.id, { photos: cleanedPhotos });
      setPhotosSaved(true);
      setTimeout(() => setPhotosSaved(false), 1500);
    }
  };

  const removePhoto = async (index: number) => {
    const photoToRemove = photos[index];
    const currentUser = getCurrentUser();

    // Delete from Firebase Storage and update usage count
    if (currentUser) {
      await deletePhoto(currentUser.uid, photoToRemove.url);
    }

    let updatedPhotos = photos.filter((_, i) => i !== index);
    if (updatedPhotos.length > 0 && !updatedPhotos.some(p => p.isDefault)) {
      updatedPhotos[0].isDefault = true;
    }
    setPhotos(updatedPhotos);
    if (onUpdate) {
      const cleanedPhotos = updatedPhotos.map(cleanPhotoForSave);
      await onUpdate(polish.id, { photos: cleanedPhotos });
      setPhotosSaved(true);
      setTimeout(() => setPhotosSaved(false), 1500);
    }
  };

  const handleSavePhotos = async () => {
    if (!onUpdate) return;
    const cleanedPhotos = photos.map(cleanPhotoForSave);
    await onUpdate(polish.id, { photos: cleanedPhotos });
    setPhotosSaved(true);
    setIsEditingPhotos(false);
    setTimeout(() => setPhotosSaved(false), 1500);
  };

  const handleCancelPhotoEdit = () => {
    setPhotos(polish.photos || []);
    setIsEditingPhotos(false);
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const currentUser = getCurrentUser();
    if (!currentUser) { alert('Please log in to upload photos'); return; }
    setIsUploadingPhoto(true);
    try {
      const newPhotos: Photo[] = [];
      for (const file of Array.from(files)) {
        const nameWithoutExt = file.name.replace(/\.(jpg|jpeg|png|gif|webp)$/i, '');
        const parts = nameWithoutExt.split(' - ');
        const caption = parts.length >= 2 ? parts.slice(1).join(' - ').trim() : '';
        
        const result = await uploadPhoto(
          currentUser.uid,
          file,
          'polishes',
          { caption: caption || undefined, isDefault: photos.length === 0 && newPhotos.length === 0 }
        );
        
        if (result.showUpgradeModal) {
          setShowStorageUpgradeModal(true);
          break;
        }
        
        if (result.success && result.photo) {
          newPhotos.push(result.photo);
        }
      }
      
      if (newPhotos.length > 0) {
        setPhotos(current => [...current, ...newPhotos]);
        if (onUpdate) {
          const allPhotos = [...photos, ...newPhotos].map(cleanPhotoForSave);
          await onUpdate(polish.id, { photos: allPhotos });
          setPhotosSaved(true);
          setTimeout(() => setPhotosSaved(false), 1500);
        }
      }
    } catch (error) {
      console.error('Error uploading photos:', error);
      alert('Failed to upload photos. Please try again.');
    } finally {
      setIsUploadingPhoto(false);
      e.target.value = '';
    }
  };

  // Close handler
  const handleClose = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to close?');
      if (!confirmed) return;
    }
    onClose();
  };

  // Navigation handlers with unsaved changes warning
  const handlePrev = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to go to the previous item?');
      if (!confirmed) return;
    }
    onPrev?.();
  };

  const handleNext = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to go to the next item?');
      if (!confirmed) return;
    }
    onNext?.();
  };

  // Get images for carousel
  const getImages = (): string[] => {
    if (photos && photos.length > 0) {
      // Respect array order (the drag-to-reorder feature persists the
      // user's chosen order). isDefault is now purely for cover-tile
      // selection on cards, not for detail-page display order.
      return photos.map(p => p.url);
    }
    return [
      ...(polish.swatchPhotos || []),
      ...(polish.bottlePhotos || []),
      ...(polish.macroPhotos || []),
      ...(polish.inUsePhotos || [])
    ];
  };

  const images = getImages();

  // Get current photo data for caption/credit display
  const getCurrentPhotoData = (): Photo | null => {
    if (photos && photos.length > 0) {
      // Respect array order (must match getImages).
      return photos[currentPhotoIndex] || null;
    }
    return null;
  };

  const currentPhoto = getCurrentPhotoData();

  // Count selected tags for collapsed summary
  const countSelectedTags = (): number => {
    let count = 0;
    if (baseType) count++;
    count += finish.length + holoType.length + particles.length + specialFeatures.length + colorFamily.length + utility.length + colorCharacter.length;
    if (colorShift && colorShift !== 'none') count++;
    return count;
  };

  // Get active toggle icons for collapsed summary
  const getActiveToggleIcons = () => {
    const icons: React.ReactElement[] = [];
    if (isFavorite) icons.push(<AppIcons.Favorite key="fav" className="w-4 h-4 text-current" aria-label="Favorite" />);
    if (onWishlist) icons.push(<AppIcons.Wishlist key="wish" className="w-4 h-4 text-current" aria-label="PublicWishlist" />);
    if (isRareLE) icons.push(<AppIcons.RareLE key="rare" className="w-4 h-4 text-current" aria-label="Rare/Limited Edition" />);
    if (needsInfo) icons.push(<AppIcons.NeedsInfo key="info" className="w-4 h-4 text-current" aria-label="Needs Information" />);
    return icons;
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 md:p-4 z-50">
      {/* Floating Previous Button */}
      {(onPrev || onNext) && (
        <button
          type="button"
          onClick={handlePrev}
          disabled={!hasPrev}
          className="fixed left-2 md:left-4 top-1/2 -translate-y-1/2 z-[60] btn-nav-light disabled:opacity-30 disabled:cursor-not-allowed"
          title="Previous item"
        >
          <ChevronLeft className="icon-base md:w-8 md:h-8 text-gray-600" />
        </button>
      )}

      {/* Floating Next Button */}
      {(onPrev || onNext) && (
        <button
          type="button"
          onClick={handleNext}
          disabled={!hasNext}
          className="fixed right-2 md:right-4 top-1/2 -translate-y-1/2 z-[60] btn-nav-light disabled:opacity-30 disabled:cursor-not-allowed"
          title="Next item"
        >
          <ChevronRight className="icon-base md:w-8 md:h-8 text-gray-600" />
        </button>
      )}

      <div className="card-light w-full max-w-5xl max-h-[95vh] overflow-y-auto overscroll-contain">
        {/* Header */}
        <div className="modal-header-light">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span className="px-2 py-1 bg-gray-700 text-white rounded-full text-xs font-bold flex-shrink-0">Polish</span>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 truncate font-heading">
              {isEditing ? `Edit: ${name}` : polish.name}
            </h2>
            {polish.isPrivate && <PrivateLockBadge variant="chip" />}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isPublicView ? (
              /* Public View - Show "Add to ISO" button instead of Edit */
              <>
                {addedToIso ? (
                  <span className="px-3 md:px-4 py-2 bg-green-100 text-green-700 rounded-lg font-medium text-sm md:text-base flex items-center gap-1">
                    <Sparkles className="icon-sm" />
                    Added as {PriorityColors[isoLevel as PriorityKey]?.label || PriorityColors.iso.label}!
                  </span>
                ) : (
                  <div className="relative">
                    <button 
                      type="button"
                      onClick={() => setShowIsoMenu(!showIsoMenu)}
                      className="px-3 md:px-4 py-2 bg-pink-500 text-white rounded-lg font-medium hover:bg-pink-600 transition-default text-sm md:text-base flex items-center gap-1"
                    >
                      <Eye className="icon-sm" />
                      Add to ISO
                      <ChevronDown className="icon-sm" />
                    </button>
                    {showIsoMenu && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-300 py-2 z-20">
                        <button
                          type="button"
                          onClick={() => handleAddToMyWishlist('iso')}
                          className="w-full px-4 py-2 text-left hover:bg-pink-50 text-gray-600 flex items-center gap-2"
                        >
                          💎 ISO <span className="text-xs text-gray-500">(In Search Of)</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleAddToMyWishlist('diso')}
                          className="w-full px-4 py-2 text-left hover:bg-rose-50 text-gray-600 flex items-center gap-2"
                        >
                          🔥 DISO <span className="text-xs text-gray-500">(Desperately)</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleAddToMyWishlist('unicorn')}
                          className="w-full px-4 py-2 text-left hover:bg-gray-50 text-gray-600 flex items-center gap-2"
                        >
                          🦄 Unicorn <span className="text-xs text-gray-500">(Grail item!)</span>
                        </button>
                      </div>
                    )}
                  </div>
                )}
                <button type="button" onClick={handleClose} className="p-2 hover:bg-gray-50 rounded-full transition-default">
                  <X className="icon-md md:w-6 md:h-6" />
                </button>
              </>
            ) : !isEditing ? (
              /* Owner View - Not Editing */
              <>
                <button 
                  type="button"
                  onClick={handleEdit}
                  className={`px-3 md:px-4 py-2 ${CategoryColors.polish.bgActive} text-white rounded-lg font-medium transition-default text-sm md:text-base`}
                >
                  Edit
                </button>
                <button type="button" onClick={handleClose} className="p-2 hover:bg-gray-50 rounded-full transition-default">
                  <X className="icon-md md:w-6 md:h-6" />
                </button>
              </>
            ) : (
              /* Owner View - Editing */
              <>
                <button 
                  type="button"
                  onClick={handleSaveEdit}
                  className="px-3 md:px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-default text-sm md:text-base"
                >
                  Save
                </button>
                <button 
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-3 md:px-4 py-2 bg-gray-500 text-white rounded-lg font-medium hover:bg-gray-600 transition-default text-sm md:text-base"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>

        {/* Main Content - Two columns on desktop */}
        <div className="p-4 md:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* LEFT COLUMN: Photos */}
            <div className="space-y-3">
              {/* Image Carousel */}
              {images.length > 0 ? (
                <ImageCarousel 
                  images={images} 
                  alt={polish.name}
                  onSlideChange={setCurrentPhotoIndex}
                />
              ) : (
                <div className="w-full aspect-square border-2 border-gray-300 rounded-lg flex items-center justify-center text-gray-500">
                  No Photos
                </div>
              )}

              {/* Caption & Credit - synced with current photo */}
              {currentPhoto && (
                <div className={`text-center py-2 px-3 border rounded-lg ${currentPhoto.dmcaStatus === 'hidden' ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}>
                  {currentPhoto.dmcaStatus === 'hidden' && (
                    <div className="text-xs font-bold text-red-600 mb-1 flex items-center justify-center gap-1">
                      ⚠️ DMCA Hidden - This photo is not visible to others
                    </div>
                  )}
                  {currentPhoto.caption && (
                    <div className="text-sm font-medium text-gray-600">{currentPhoto.caption}</div>
                  )}
                  {currentPhoto.creditName && (
                    <div className="text-xs text-gray-500 mt-1">
                      📷 {currentPhoto.creditUrl ? (
                        <a 
                          href={currentPhoto.creditUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className={`${CategoryColors.polish.text} ${CategoryColors.polish.textHover} underline`}
                        >
                          {currentPhoto.creditName} ↗
                        </a>
                      ) : currentPhoto.creditName}
                    </div>
                  )}
                  {!currentPhoto.caption && !currentPhoto.creditName && !currentPhoto.dmcaStatus && (
                    <div className="text-xs text-gray-500 italic">No caption</div>
                  )}
                </div>
              )}

              {/* Photo Actions - Only show for owner */}
              {!isPublicView && (
                <div className="flex gap-2">
                  {photos.length > 0 && !isEditingPhotos && (
                    <button
                      type="button"
                      onClick={() => setIsEditingPhotos(true)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-200 transition-default"
                    >
                      <AppIcons.EditAction className="w-4 h-4 text-current" aria-label="Edit" />
                      <span>Edit Photos</span>
                    </button>
                  )}
                  <label className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm rounded-lg cursor-pointer transition-default ${
                    isUploadingPhoto ? 'border-2 border-gray-300 text-gray-500' : `${CategoryColors.polish.light} ${CategoryColors.polish.text} hover:bg-indigo-100`
                  }`}>
                    {isUploadingPhoto ? (
                      <span>Uploading...</span>
                    ) : (
                      <>
                        <Plus className="icon-sm" />
                        <span>Add Photos</span>
                      </>
                    )}
                    <input type="file" accept={ACCEPTED_IMAGE_TYPES} multiple onChange={handlePhotoUpload} disabled={isUploadingPhoto} className="hidden" />
                  </label>
                </div>
              )}

              {/* Photo Editor (when editing) - Only for owner */}
              {!isPublicView && isEditingPhotos && (
                <div className={`border ${CategoryColors.polish.lightBorder} rounded-lg p-3 ${CategoryColors.polish.light} space-y-3`}>
                  <div className="flex items-center justify-between">
                    <span className={`font-medium ${CategoryColors.polish.text} text-sm`}>Editing Photos</span>
                    <div className="flex gap-2">
                      <button type="button" onClick={handleSavePhotos} className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700">Save</button>
                      <button type="button" onClick={handleCancelPhotoEdit} className="px-3 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600">Cancel</button>
                    </div>
                  </div>
                  <SortablePhotoGrid
                    items={photos}
                    onReorder={setPhotos}
                    getId={(p) => p.url}
                    className="space-y-2 max-h-64 overflow-y-auto"
                  >
                    {(photo, idx, dragHandleProps) => (
                      <div className={`flex gap-2 p-2 rounded ${photo.isDefault ? 'bg-yellow-100 border border-yellow-300' : 'bg-white border border-gray-300'} ${photo.dmcaStatus === 'hidden' ? 'ring-2 ring-red-500' : ''}`}>
                        <button
                          type="button"
                          {...dragHandleProps}
                          className="flex-shrink-0 self-center px-1 cursor-grab active:cursor-grabbing touch-none text-gray-400 hover:text-gray-600"
                          title="Drag to reorder"
                          aria-label="Drag to reorder"
                        >
                          <GripVertical className="w-4 h-4" />
                        </button>
                        <div className="relative flex-shrink-0">
                          <img src={photo.url} alt="" className="w-12 h-12 object-cover rounded" />
                          {photo.dmcaStatus === 'hidden' && (
                            <div className="absolute inset-0 bg-red-500/70 rounded flex items-center justify-center">
                              <span className="text-white text-[8px] font-bold">DMCA</span>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 space-y-1 min-w-0">
                          <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0 text-xs text-gray-700">
                              {buildPhotoSummary(photo) || (
                                <span className="text-gray-400 italic">No details yet.</span>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => setEditingPhotoIdx(idx)}
                              className="flex-shrink-0 px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 flex items-center gap-1"
                              title="Edit caption, credit, skin tone, lighting"
                            >
                              <Pencil className="w-3 h-3" />
                              Edit
                            </button>
                          </div>
                          <div className="flex gap-1">
                            {!photo.isDefault && (
                              <button type="button" onClick={() => setDefaultPhoto(idx)} className="px-2 py-0.5 text-xs bg-yellow-200 text-yellow-800 rounded hover:bg-yellow-300">⭐ Default</button>
                            )}
                            <button type="button" onClick={() => removePhoto(idx)} className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 flex items-center gap-1">
                              <AppIcons.Delete className="w-3 h-3 text-current" aria-label="Delete" />
                              <span>Remove</span>
                            </button>
                          </div>
                          {/* Legacy rights-attestation toggle. Only shown
                              for photos uploaded before the rights checkbox
                              existed. Toggling on marks this specific photo
                              eligible for tile-representative use across
                              discovery — never written as `false` (absence
                              === grandfathered). */}
                          {!!photo.photoRightsAttested ? (
                            <div className="flex items-center gap-1 text-[10px] text-emerald-700 mt-0.5">
                              <span>✓ Rights attested</span>
                            </div>
                          ) : (
                            <label className="flex items-start gap-1.5 text-[10px] text-gray-600 mt-0.5 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={false}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    updatePhoto(idx, { photoRightsAttested: true });
                                  }
                                }}
                                className="mt-0.5"
                              />
                              <span>
                                I have the right to share this image (allows it to represent this polish across discovery tiles)
                              </span>
                            </label>
                          )}
                        </div>
                      </div>
                    )}
                  </SortablePhotoGrid>
                  <PhotoEditModal
                    open={editingPhotoIdx !== null}
                    photo={(() => {
                      const p = editingPhotoIdx !== null ? photos[editingPhotoIdx] : null;
                      if (!p) return null;
                      return {
                        imageUri: p.url,
                        caption: p.caption,
                        creditName: p.creditName,
                        creditUrl: p.creditUrl,
                        skinTone: p.skinTone,
                        lighting: p.lighting,
                      };
                    })()}
                    onClose={() => setEditingPhotoIdx(null)}
                    onSave={(patch) => {
                      if (editingPhotoIdx === null) return;
                      updatePhoto(editingPhotoIdx, {
                        caption: patch.caption,
                        creditName: patch.creditName,
                        creditUrl: patch.creditUrl,
                        skinTone: patch.skinTone,
                        lighting: patch.lighting,
                      });
                    }}
                  />
                </div>
              )}
            </div>

            {/* RIGHT COLUMN: Basic Info */}
            <div className="space-y-4">
              {/* Brand */}
              <div>
                <label className="section-header-light">Brand</label>
                {isEditing ? (
                  <BrandAutocomplete
                    value={brand}
                    onChange={setBrand}
                    existingBrands={existingBrands}
                    userBrandCategories={userBrandCategories}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`}
                  />
                ) : (
                  <div className="text-lg font-semibold text-gray-900">{polish.brand}</div>
                )}
              </div>

              {/* Name (only in edit mode, since it's in header) */}
              {isEditing && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Name</label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`} />
                </div>
              )}

              {/* Category & Quantity Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Category</label>
                  {isEditing ? (
                    <select value={category} onChange={(e) => setCategory(e.target.value)} className={`w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`}>
                      <option value="">Select...</option>
                      <option value="Base Coat">Base Coat</option>
                      <option value="Builder Gel">Builder Gel</option>
                      <option value="Dip Powder">Dip Powder</option>
                      <option value="Gel Polish">Gel Polish</option>
                      <option value="Gel Stamping Polish">Gel Stamping Polish</option>
                      <option value="Nail Lacquer">Nail Lacquer</option>
                      <option value="Poly Gel">Poly Gel</option>
                      <option value="Stamping Polish">Stamping Polish</option>
                      <option value="Top Coat">Top Coat</option>
                      <option value="Treatment">Treatment</option>
                    </select>
                  ) : (
                    <div className="text-gray-900">{polish.category || '—'}</div>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Quantity</label>
                  {isEditing ? (
                    <input type="number" min="0" value={quantity} onChange={(e) => setQuantity(e.target.value)} className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`} />
                  ) : (
                    <div className="text-gray-900">{polish.quantity ?? 0}</div>
                  )}
                </div>
              </div>

              {/* Color Description */}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Color Description</label>
                {isEditing ? (
                  <textarea value={colorDescription} onChange={(e) => setColorDescription(e.target.value)} rows={2} className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent resize-none`} />
                ) : (
                  <div className="text-gray-600 text-sm">{polish.colorDescription || '—'}</div>
                )}
              </div>

              {/* Line */}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Line</label>
                {isEditing ? (
                  <input type="text" value={line_} onChange={(e) => setLine_(e.target.value)} placeholder="e.g., Ultra Chrome, Juicy Collection" className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`} />
                ) : (
                  polish.line ? <div className="text-gray-900 text-sm">{polish.line}</div> : null
                )}
              </div>

              {/* ✨ RESTRUCTURED: Release Info Section - Clean conditional fields */}
              <div className={`${CategoryColors.polish.light} border ${CategoryColors.polish.lightBorder} rounded-lg p-4`}>
                <label className={`text-xs font-medium ${CategoryColors.polish.text} uppercase tracking-wide flex items-center gap-2 mb-3`}>
                  📅 Release Info
                </label>
                
                {!isEditing ? (
                  /* VIEW MODE - Display based on release type */
                  <div className="space-y-2">
                    {/* Release type badge */}
                    {releaseType && releaseType !== 'unknown' && (
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          releaseType === 'core' ? 'bg-gray-100 text-gray-700' :
                          releaseType === 'collection' ? 'bg-purple-100 text-purple-700' :
                          releaseType === 'event' ? 'bg-pink-100 text-pink-700' :
                          releaseType === 'box' ? 'bg-amber-100 text-amber-700' :
                          releaseType === 'groupCustom' ? 'bg-blue-100 text-blue-700' :
                          releaseType === 'exclusive' ? 'bg-yellow-100 text-yellow-700' :
                          releaseType === 'charity' ? 'bg-rose-100 text-rose-700' :
                          releaseType === 'limitedEdition' ? 'bg-indigo-100 text-indigo-700' :
                          releaseType === 'convention' ? 'bg-teal-100 text-teal-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {releaseType === 'core' && '🏠 Core/Permanent'}
                          {releaseType === 'collection' && '📚 Collection'}
                          {releaseType === 'event' && '🎪 Event'}
                          {releaseType === 'box' && '📦 Box'}
                          {releaseType === 'groupCustom' && '👥 Group Custom'}
                          {releaseType === 'exclusive' && '⭐ Exclusive'}
                          {releaseType === 'charity' && '💝 Charity'}
                          {releaseType === 'limitedEdition' && '✨ Limited Edition'}
                          {releaseType === 'convention' && '🎫 Convention'}
                          {releaseType === 'other' && '📝 Other'}
                        </span>
                      </div>
                    )}
                    
                    {/* Collection info */}
                    {polish.collectionName && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-500 uppercase">Collection:</span>
                        <span className="font-semibold text-purple-700">{polish.collectionName}</span>
                      </div>
                    )}
                    
                    {/* Event info (check both old and new field names) */}
                    {(polish.eventName || polish.eventSource) && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-500 uppercase">Event:</span>
                        <span className="font-semibold text-pink-700">{polish.eventName || polish.eventSource}</span>
                        {polish.eventTheme && (
                          <span className="text-gray-600">— {polish.eventTheme}</span>
                        )}
                      </div>
                    )}
                    
                    {/* Box info (check both old and new field names) */}
                    {(polish.boxName || polish.box) && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-500 uppercase">Box:</span>
                        <span className="font-semibold text-amber-700">{polish.boxName || polish.box}</span>
                        {polish.boxTheme && (
                          <span className="text-gray-600">— {polish.boxTheme}</span>
                        )}
                      </div>
                    )}
                    
                    {/* Group Custom info */}
                    {polish.groupName && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-500 uppercase">Group:</span>
                        <span className="font-semibold text-blue-700">{polish.groupName}</span>
                        {polish.groupTheme && (
                          <span className="text-gray-600">— {polish.groupTheme}</span>
                        )}
                      </div>
                    )}
                    
                    {/* Exclusive info */}
                    {(polish.exclusiveTo || polish.exclusiveTheme) && (
                      <div className="flex flex-col gap-1">
                        {polish.exclusiveTo && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-500 uppercase">Exclusive to:</span>
                            <span className="font-semibold text-yellow-700">{polish.exclusiveTo}</span>
                          </div>
                        )}
                        {polish.exclusiveTheme && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-500 uppercase">Excl. Theme:</span>
                            <span className="font-semibold text-yellow-600">{polish.exclusiveTheme}</span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Charity info */}
                    {(polish.charityName || polish.charityCause) && (
                      <div className="flex flex-col gap-1">
                        {polish.charityName && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-500 uppercase">Charity:</span>
                            <span className="font-semibold text-rose-700">{polish.charityName}</span>
                          </div>
                        )}
                        {polish.charityCause && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-500 uppercase">Cause:</span>
                            <span className="font-semibold text-rose-600">{polish.charityCause}</span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Convention info */}
                    {polish.conventionName && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-500 uppercase">Convention:</span>
                        <span className="font-semibold text-teal-700">{polish.conventionName}</span>
                      </div>
                    )}
                    
                    {/* Collab - always show if present */}
                    {(polish.collabWith || polish.collab || polish.collabTheme) && (
                      <div className="flex flex-col gap-1">
                        {(polish.collabWith || polish.collab) && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-500 uppercase">Collab With:</span>
                            <span className="font-semibold text-violet-700">{polish.collabWith || polish.collab}</span>
                          </div>
                        )}
                        {polish.collabTheme && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-500 uppercase">Collab Theme:</span>
                            <span className="font-semibold text-violet-600">{polish.collabTheme}</span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* No info fallback */}
                    {!polish.collectionName && !polish.eventName && !polish.eventSource && !polish.boxName && !polish.box && !polish.groupName && !polish.exclusiveTo && !polish.exclusiveTheme && !polish.charityName && !polish.charityCause && !polish.conventionName && !polish.collabWith && !polish.collab && releaseType !== 'core' && releaseType !== 'limitedEdition' && (
                      <div className="text-gray-500 italic">No release info</div>
                    )}
                    
                    {/* Season & Year badge */}
                    {(polish.releaseSeason || polish.releaseYear || polish.releaseMonth) && (() => {
                      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                      const monthToSeason: Record<number, string> = {
                        1: 'Winter', 2: 'Winter', 3: 'Spring', 4: 'Spring', 5: 'Spring',
                        6: 'Summer', 7: 'Summer', 8: 'Summer', 9: 'Fall', 10: 'Fall', 11: 'Fall', 12: 'Winter'
                      };
                      
                      // Determine effective season (from month if available, otherwise from releaseSeason)
                      const effectiveSeason = polish.releaseMonth 
                        ? monthToSeason[polish.releaseMonth] 
                        : polish.releaseSeason;
                      
                      // Build display text: prefer month over season (more specific)
                      let text = '';
                      if (polish.releaseMonth) {
                        text = monthNames[polish.releaseMonth - 1];
                      } else if (polish.releaseSeason) {
                        text = polish.releaseSeason;
                      }
                      if (polish.releaseYear) {
                        text = text ? `${text} ${polish.releaseYear}` : `${polish.releaseYear}`;
                      }
                      
                      return (
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                            effectiveSeason === 'Spring' ? 'bg-pink-100 text-pink-700' :
                            effectiveSeason === 'Summer' ? 'bg-yellow-100 text-yellow-700' :
                            effectiveSeason === 'Fall' ? 'bg-orange-100 text-orange-700' :
                            effectiveSeason === 'Winter' ? 'bg-blue-100 text-blue-700' :
                            'border-2 border-gray-300 text-gray-600'
                          }`}>
                            {effectiveSeason === 'Spring' && '🌷'}
                            {effectiveSeason === 'Summer' && '☀️'}
                            {effectiveSeason === 'Fall' && '🍂'}
                            {effectiveSeason === 'Winter' && '❄️'}
                            {!effectiveSeason && '📅'}
                            {` ${text}`}
                          </span>
                        </div>
                      );
                    })()}
                    
                    {/* Re-release info */}
                    {polish.isRerelease && (
                      <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-center gap-2 text-amber-700 text-sm font-medium">
                          🔄 Re-release
                          {(polish.originalReleaseYear || polish.originalReleaseSeason) && (
                            <span className="text-amber-600 font-normal">
                              (Originally: {polish.originalReleaseSeason && `${polish.originalReleaseSeason} `}{polish.originalReleaseYear})
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Release notes */}
                    {polish.releaseNotes && (
                      <div className="mt-2 text-sm text-gray-600 italic">
                        📝 {polish.releaseNotes}
                      </div>
                    )}
                    
                    {/* Duo/Trio/Set info */}
                    {polish.setType && (
                      <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-center gap-2 text-amber-700 text-sm font-medium">
                          👯 {polish.setType === 'duo' ? 'Duo' : polish.setType === 'trio' ? 'Trio' : polish.setType === 'quad' ? 'Quad' : 'Set'}
                          {polish.setName && (
                            <span className="text-amber-600 font-normal">
                              — {polish.setName}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* EDIT MODE - Release type dropdown with conditional fields */
                  <div className="space-y-3">
                    {/* Release Type Dropdown */}
                    <div>
                      <label className={`text-xs ${CategoryColors.polish.text} block mb-1`}>Release Type</label>
                      <select
                        value={releaseType}
                        onChange={(e) => setReleaseType(e.target.value as ReleaseType)}
                        className={`w-full px-3 py-2 bg-white text-gray-900 border ${CategoryColors.polish.lightBorder} rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`}
                      >
                        <option value="unknown">❓ Unknown</option>
                        <option value="core">🏠 Core / Permanent</option>
                        <option value="collection">📚 Collection</option>
                        <option value="event">🎪 Event</option>
                        <option value="box">📦 Box</option>
                        <option value="groupCustom">👥 Group Custom</option>
                        <option value="exclusive">⭐ Exclusive</option>
                        <option value="charity">💝 Charity</option>
                        <option value="limitedEdition">✨ Limited Edition</option>
                        <option value="convention">🎫 Convention</option>
                        <option value="other">📝 Other</option>
                      </select>
                    </div>
                    
                    {/* Conditional Fields Based on Release Type */}
                    {releaseType === 'collection' && (
                      <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <label className="block text-sm font-medium text-purple-700 mb-1">📚 Collection Name</label>
                        <input type="text" value={collectionName} onChange={(e) => setCollectionName(e.target.value)} placeholder="e.g., Enchanted Forest" className={`w-full px-3 py-2 border ${CategoryColors.polish.lightBorder} rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`} />
                      </div>
                    )}
                    
                    {releaseType === 'event' && (
                      <div className="p-3 bg-pink-50 border border-pink-200 rounded-lg space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-pink-700 mb-1">🎪 Event Name</label>
                            <input type="text" value={eventName} onChange={(e) => setEventName(e.target.value)} placeholder="e.g., Polish Pickup" className={`w-full px-3 py-2 border ${CategoryColors.polish.lightBorder} rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`} />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-pink-700 mb-1">🎨 Event Theme</label>
                            <input type="text" value={eventTheme} onChange={(e) => setEventTheme(e.target.value)} placeholder="e.g., Periodic Table" className={`w-full px-3 py-2 border ${CategoryColors.polish.lightBorder} rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`} />
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {releaseType === 'box' && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-amber-700 mb-1">📦 Box Name</label>
                            <input type="text" value={boxName} onChange={(e) => setBoxName(e.target.value)} placeholder="e.g., Polished Gamers Box" className={`w-full px-3 py-2 border ${CategoryColors.polish.lightBorder} rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`} />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-amber-700 mb-1">🎨 Box Theme</label>
                            <input type="text" value={boxTheme} onChange={(e) => setBoxTheme(e.target.value)} placeholder="e.g., October 2024" className={`w-full px-3 py-2 border ${CategoryColors.polish.lightBorder} rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`} />
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {releaseType === 'groupCustom' && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-blue-700 mb-1">👥 Group Name</label>
                            <input type="text" value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="e.g., Reddit Lacqueristas" className={`w-full px-3 py-2 border ${CategoryColors.polish.lightBorder} rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`} />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-blue-700 mb-1">🎨 Theme <span className="font-normal text-blue-500">(optional)</span></label>
                            <input type="text" value={groupTheme} onChange={(e) => setGroupTheme(e.target.value)} placeholder="e.g., Anniversary Custom" className={`w-full px-3 py-2 border ${CategoryColors.polish.lightBorder} rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`} />
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {releaseType === 'exclusive' && (
                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-yellow-700 mb-1">⭐ Exclusive To</label>
                          <input type="text" value={exclusiveTo} onChange={(e) => setExclusiveTo(e.target.value)} placeholder="e.g., Color4Nails, Holo Taco website" className={`w-full px-3 py-2 border ${CategoryColors.polish.lightBorder} rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-yellow-700 mb-1">🏷️ Exclusive Theme <span className="font-normal text-yellow-500">(optional)</span></label>
                          <input type="text" value={exclusiveTheme} onChange={(e) => setExclusiveTheme(e.target.value)} placeholder="e.g., Black Friday 2024, Launch Exclusive" className={`w-full px-3 py-2 border ${CategoryColors.polish.lightBorder} rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`} />
                        </div>
                      </div>
                    )}
                    
                    {releaseType === 'charity' && (
                      <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-rose-700 mb-1">💝 Charity Name</label>
                          <input type="text" value={charityName} onChange={(e) => setCharityName(e.target.value)} placeholder="e.g., Autism Speaks, Red Cross" className={`w-full px-3 py-2 border ${CategoryColors.polish.lightBorder} rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-rose-700 mb-1">🎗️ Cause <span className="font-normal text-rose-500">(optional)</span></label>
                          <input type="text" value={charityCause} onChange={(e) => setCharityCause(e.target.value)} placeholder="e.g., Autism Awareness, Cancer Research" className={`w-full px-3 py-2 border ${CategoryColors.polish.lightBorder} rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`} />
                        </div>
                      </div>
                    )}
                    
                    {releaseType === 'convention' && (
                      <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
                        <label className="block text-sm font-medium text-teal-700 mb-1">🎫 Convention Name</label>
                        <input type="text" value={conventionName} onChange={(e) => setConventionName(e.target.value)} placeholder="e.g., Polish Con, Indie Expo" className={`w-full px-3 py-2 border ${CategoryColors.polish.lightBorder} rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`} />
                      </div>
                    )}
                    
                    {/* Always Available: Year, Month & Season */}
                    <div className="grid grid-cols-3 gap-3 mt-3">
                      <div>
                        <label className={`text-xs ${CategoryColors.polish.text} block mb-1`}>Year</label>
                        <input type="number" value={releaseYear} onChange={(e) => setReleaseYear(e.target.value)} placeholder="e.g., 2024" min="2000" max="2030" className={`w-full px-3 py-2 border ${CategoryColors.polish.lightBorder} rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`} />
                      </div>
                      <div>
                        <label className={`text-xs ${CategoryColors.polish.text} block mb-1`}>Month</label>
                        <select value={releaseMonth} onChange={(e) => setReleaseMonth(e.target.value)} className={`w-full px-3 py-2 bg-white text-gray-900 border ${CategoryColors.polish.lightBorder} rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`}>
                          <option value="">Unknown</option>
                          <option value="1">❄️ January</option>
                          <option value="2">❄️ February</option>
                          <option value="3">🌷 March</option>
                          <option value="4">🌷 April</option>
                          <option value="5">🌷 May</option>
                          <option value="6">☀️ June</option>
                          <option value="7">☀️ July</option>
                          <option value="8">☀️ August</option>
                          <option value="9">🍂 September</option>
                          <option value="10">🍂 October</option>
                          <option value="11">🍂 November</option>
                          <option value="12">❄️ December</option>
                        </select>
                      </div>
                      <div>
                        <label className={`text-xs ${CategoryColors.polish.text} block mb-1`}>Season</label>
                        <select value={releaseSeason} onChange={(e) => setReleaseSeason(e.target.value)} className={`w-full px-3 py-2 bg-white text-gray-900 border ${CategoryColors.polish.lightBorder} rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`}>
                          <option value="">Unknown</option>
                          <option value="Spring">🌷 Spring</option>
                          <option value="Summer">☀️ Summer</option>
                          <option value="Fall">🍂 Fall</option>
                          <option value="Winter">❄️ Winter</option>
                        </select>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Month takes priority over season if both are set</p>
                    
                    {/* Re-release Section */}
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isRerelease}
                          onChange={(e) => setIsRerelease(e.target.checked)}
                          className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                        />
                        <span className="text-sm font-medium text-amber-700">
                          🔄 This is a re-release of an earlier polish
                        </span>
                      </label>
                      
                      {isRerelease && (
                        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-amber-200">
                          <div>
                            <label className="block text-sm font-medium text-amber-700 mb-1">Original Year</label>
                            <input
                              type="number"
                              value={originalReleaseYear}
                              onChange={(e) => setOriginalReleaseYear(e.target.value)}
                              placeholder="e.g., 2019"
                              min="2000"
                              max="2030"
                              className={`w-full px-3 py-2 border ${CategoryColors.polish.lightBorder} rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-amber-700 mb-1">Original Season</label>
                            <select
                              value={originalReleaseSeason}
                              onChange={(e) => setOriginalReleaseSeason(e.target.value)}
                              className={`w-full px-3 py-2 bg-white text-gray-900 border ${CategoryColors.polish.lightBorder} rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`}
                            >
                              <option value="">Select season...</option>
                              <option value="Spring">🌷 Spring</option>
                              <option value="Summer">☀️ Summer</option>
                              <option value="Fall">🍂 Fall</option>
                              <option value="Winter">❄️ Winter</option>
                            </select>
                          </div>
                        </div>
                      )}
                      
                      <div>
                        <label className="block text-sm font-medium text-amber-700 mb-1">
                          📝 Release Notes <span className="font-normal text-amber-500">(optional)</span>
                        </label>
                        <textarea
                          value={releaseNotes}
                          onChange={(e) => setReleaseNotes(e.target.value)}
                          placeholder="e.g., New label art, slightly tweaked formula, limited restock..."
                          rows={2}
                          className={`w-full px-3 py-2 border ${CategoryColors.polish.lightBorder} rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent text-sm`}
                        />
                      </div>
                    </div>
                    
                    {/* Always Available: Collab */}
                    <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-violet-700 mb-1">🤝 Collab With <span className="text-xs text-violet-400">(can apply to any type)</span></label>
                        <input type="text" value={collabWith} onChange={(e) => setCollabWith(e.target.value)} placeholder="e.g., @gotnail, @varnishedvampire, Simply Nailogical" className={`w-full px-3 py-2 border ${CategoryColors.polish.lightBorder} rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-violet-700 mb-1">🎨 Collab Theme <span className="font-normal text-violet-400">(optional)</span></label>
                        <input type="text" value={collabTheme} onChange={(e) => setCollabTheme(e.target.value)} placeholder="e.g., Halloween 2024, Anniversary Collection" className={`w-full px-3 py-2 border ${CategoryColors.polish.lightBorder} rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`} />
                      </div>
                    </div>
                    
                    {/* Always Available: Duo/Trio/Set */}
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-amber-700 mb-1">👯 Set Type <span className="text-xs text-amber-400">(if part of a set)</span></label>
                          <select value={setType} onChange={(e) => setSetType(e.target.value as typeof setType)} className={`w-full px-3 py-2 border ${CategoryColors.polish.lightBorder} rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`}>
                            <option value="">Not part of a set</option>
                            <option value="duo">Duo (2 polishes)</option>
                            <option value="trio">Trio (3 polishes)</option>
                            <option value="quad">Quad (4 polishes)</option>
                            <option value="set">Larger Set (5+)</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-amber-700 mb-1">🏷️ Set Name <span className="font-normal text-amber-400">(optional)</span></label>
                          <input type="text" value={setNameField} onChange={(e) => setSetNameField(e.target.value)} placeholder="e.g., Autumn Duo, Holiday Trio" className={`w-full px-3 py-2 border ${CategoryColors.polish.lightBorder} rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`} disabled={!setType} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Storage & Price Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Storage</label>
                  {isEditing ? (
                    <input type="text" value={storage_} onChange={(e) => setStorage_(e.target.value)} className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`} />
                  ) : (
                    <div className="text-gray-900 text-sm">{polish.storage || '—'}</div>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Purchase Price</label>
                  {isEditing ? (
                    <>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{purchaseSymbol}</span>
                        <input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className={`w-full ${purchaseSymbolPad} pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`} />
                      </div>
                      <p className="mt-1 text-xs text-gray-700 italic">
                        Defaults to your profile currency. Change per item if you paid in a different currency.
                      </p>
                    </>
                  ) : (
                    <div className="text-gray-900 text-sm">{polish.price != null ? formatPrice(polish.price, polish.priceCurrency ?? currency.code) : '—'}</div>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Currency</label>
                  {isEditing ? (
                    <CurrencyPicker
                      value={purchaseCurrency}
                      onChange={setPurchaseCurrency}
                      pinnedCurrencies={pinnedCurrencies}
                      onPinChange={setPinnedCurrencies}
                      sellerDefault={currency.code}
                    />
                  ) : (
                    <div className="text-gray-900 text-sm">
                      {polish.priceCurrency ? getCurrencyInfo(polish.priceCurrency).label : '—'}
                    </div>
                  )}
                </div>
              </div>

              {/* Purchase Date — matches the AddPolishModal layout so add
                  and edit show the same set of inventory fields. */}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Purchase Date</label>
                {isEditing ? (
                  <input
                    type="date"
                    value={purchaseDate}
                    onChange={(e) => setPurchaseDate(e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`}
                  />
                ) : (
                  <div className="text-gray-900 text-sm">
                    {(() => {
                      const v: any = polish.purchaseDate;
                      if (!v) return '—';
                      try {
                        const d = v instanceof Date ? v : typeof v?.toDate === 'function' ? v.toDate() : new Date(v);
                        return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
                      } catch { return '—'; }
                    })()}
                  </div>
                )}
              </div>

              {/* Barcode & Swatch Number (edit mode, or display if has value) */}
              {(isEditing || polish.swatchNumber) && (
                <div className={`grid ${isEditing ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}>
                  {isEditing && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Barcode/SKU</label>
                      <input type="text" value={barcode} onChange={(e) => setBarcode(e.target.value)} className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent font-mono`} />
                    </div>
                  )}
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Swatch Number</label>
                    {isEditing ? (
                      <input type="text" value={swatchNumber} onChange={(e) => setSwatchNumber(e.target.value)} placeholder="e.g., 1234" className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`} />
                    ) : (
                      <div className="text-gray-900 text-sm">{polish.swatchNumber}</div>
                    )}
                  </div>
                </div>
              )}

              {/* USAGE TRACKER - Owner only */}
              {!isPublicView && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
                {!isEditingUsage ? (
                  /* View Mode */
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold text-green-700">{timesUsed}</span>
                        <span className="text-sm text-green-600">times used</span>
                        <button
                          type="button"
                          onClick={() => setIsEditingUsage(true)}
                          className="text-xs text-green-600 hover:text-green-800 underline ml-2"
                        >
                          edit
                        </button>
                      </div>
                      {lastUsedDate && (
                        <div className="text-xs text-green-600 mt-1">
                          Last: {(() => {
                            const date = parseDate(lastUsedDate);
                            return date ? date.toLocaleDateString() : 'Invalid date';
                          })()}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleLogUsage}
                      className={`px-4 py-3 rounded-lg font-bold text-lg transition-all ${
                        usageSaved 
                          ? 'bg-green-600 text-white scale-95' 
                          : 'bg-green-500 text-white hover:bg-green-600 hover:scale-105 active:scale-95'
                      }`}
                    >
                      {usageSaved ? '✓' : '+1'}
                    </button>
                  </div>
                ) : (
                  /* Edit Mode */
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-green-800">Edit Usage</span>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleSaveUsageEdit}
                          className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setIsEditingUsage(false);
                            setTimesUsed(polish.timesUsed || 0);
                            setLastUsedDate(polish.lastUsedDate);
                          }}
                          className="px-3 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-green-700 block mb-1">Times Used</label>
                        <input
                          type="number"
                          min="0"
                          value={timesUsed}
                          onChange={(e) => setTimesUsed(parseInt(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-green-700 block mb-1">Last Used Date</label>
                        <input
                          type="date"
                          value={(() => {
                            const date = parseDate(lastUsedDate);
                            if (!date) return '';
                            return date.toISOString().split('T')[0];
                          })()}
                          onChange={(e) => {
                            if (e.target.value) {
                              // Convert to Date with noon UTC to avoid timezone date-shift
                              setLastUsedDate(new Date(e.target.value + 'T12:00:00.000Z'));
                            } else {
                              setLastUsedDate(undefined);
                            }
                          }}
                          className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              )}
            </div>
          </div>

          {/* COLLAPSIBLE SECTIONS - Full width below */}
          <div className="mt-6 space-y-2">
            
            {/* Tags Section */}
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => toggleSection('tags')}
                className="w-full flex items-center justify-between px-4 py-3 border border-gray-200 hover:bg-gray-50 transition-default"
              >
                <div className="flex items-center gap-2">
                  <AppIcons.Tags className="w-5 h-5 text-current" aria-label="Tags" />
                  <span className="font-medium text-gray-900">Tags</span>
                  {countSelectedTags() > 0 && (
                    <span className={`px-2 py-0.5 ${CategoryColors.polish.light} ${CategoryColors.polish.text} text-xs font-bold rounded-full`}>
                      {countSelectedTags()}
                    </span>
                  )}
                  {tagsSaved && <span className="text-green-600 text-xs">✓ Saved</span>}
                </div>
                {expandedSections.has('tags') ? <ChevronDown className="icon-md text-gray-500" /> : <ChevronRight className="icon-md text-gray-500" />}
              </button>
              {expandedSections.has('tags') && (
                <div className="p-4 bg-white">
                  <TagSelector
                    baseType={baseType} finish={finish} holoType={holoType} colorShift={colorShift}
                    particles={particles} specialFeatures={specialFeatures} colorFamily={colorFamily}
                    utility={utility} colorCharacter={colorCharacter}
                    onChange={handleTagChange}
                    defaultExpanded={[]}
                  />
                </div>
              )}
            </div>

            {/* Custom Tags Section — freeform user labels backed by
                item.tags[]. Distinct from the Tags section above,
                which manages structured polish categories via
                TagSelector. Auto-saves on every change, mirroring the
                Tags section's "✓ Saved" behavior. */}
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => toggleSection('customTags')}
                className="w-full flex items-center justify-between px-4 py-3 border border-gray-200 hover:bg-gray-50 transition-default"
              >
                <div className="flex items-center gap-2">
                  <AppIcons.Tags className="w-5 h-5 text-current" aria-label="Custom tags" />
                  <span className="font-medium text-gray-900">Custom tags</span>
                  {customTags.length > 0 && (
                    <span className={`px-2 py-0.5 ${CategoryColors.polish.light} ${CategoryColors.polish.text} text-xs font-bold rounded-full`}>
                      {customTags.length}
                    </span>
                  )}
                  {customTagsSaved && <span className="text-green-600 text-xs">✓ Saved</span>}
                </div>
                {expandedSections.has('customTags') ? <ChevronDown className="icon-md text-gray-500" /> : <ChevronRight className="icon-md text-gray-500" />}
              </button>
              {expandedSections.has('customTags') && (
                <div className="p-4 bg-white space-y-2">
                  <div className="text-xs text-gray-500">
                    Freeform labels for anything not covered by the structured
                    categories above — e.g. "gift", "wedding manicure",
                    "spring 2025". Also where legacy imports from Airtable
                    or CSV end up.
                  </div>
                  <CustomTagsEditor
                    value={customTags}
                    onChange={handleCustomTagsChange}
                    placeholder="Add a tag and press Enter or comma"
                  />
                </div>
              )}
            </div>

            {/* Notes Section */}
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => toggleSection('notes')}
                className="w-full flex items-center justify-between px-4 py-3 border border-gray-200 hover:bg-gray-50 transition-default"
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg">📝</span>
                  <span className="font-medium text-gray-900">Notes</span>
                  {isPublicView ? (
                    publicNotes && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">Has notes</span>
                  ) : (
                    (publicNotes || privateNotes) && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">Has notes</span>
                    )
                  )}
                  {notesSaved && <span className="text-green-600 text-xs">✓ Saved</span>}
                </div>
                {expandedSections.has('notes') ? <ChevronDown className="icon-md text-gray-500" /> : <ChevronRight className="icon-md text-gray-500" />}
              </button>
              {expandedSections.has('notes') && (
                <div className="p-4 bg-white space-y-4">
                  {isPublicView ? (
                    /* Public View - Read only public notes */
                    publicNotes ? (
                      <div className="text-gray-600">{publicNotes}</div>
                    ) : (
                      <div className="text-gray-500 italic">No public notes</div>
                    )
                  ) : (
                    /* Owner View - Editable notes */
                    <>
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Public Notes</label>
                        <textarea
                          value={publicNotes}
                          onChange={(e) => setPublicNotes(e.target.value)}
                          rows={2}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent resize-none`}
                          placeholder="Notes visible to others..."
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Private Notes 🔒</label>
                        <textarea
                          value={privateNotes}
                          onChange={(e) => setPrivateNotes(e.target.value)}
                          rows={2}
                          className={`w-full px-3 py-2 border border-yellow-200 bg-yellow-50 rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent resize-none`}
                          placeholder="Only you can see these..."
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleSaveNotes}
                        className={`w-full py-2 rounded-lg font-medium transition-default ${notesSaved ? 'bg-green-600 text-white' : `${CategoryColors.polish.bgActive} text-white`}`}
                      >
                        {notesSaved ? '✓ Saved!' : 'Save Notes'}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Toggles Section - Owner only */}
            {!isPublicView && (
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => toggleSection('toggles')}
                className="w-full flex items-center justify-between px-4 py-3 border border-gray-200 hover:bg-gray-50 transition-default"
              >
                <div className="flex items-center gap-2">
                  <AppIcons.Controls className="w-5 h-5 text-current" aria-label="Controls" />
                  <span className="font-medium text-gray-900">Toggles</span>
                  {getActiveToggleIcons().length > 0 && (
                    <span className="flex gap-1">{getActiveToggleIcons()}</span>
                  )}
                  {toggleSaved && <span className="text-green-600 text-xs">✓ Saved</span>}
                </div>
                {expandedSections.has('toggles') ? <ChevronDown className="icon-md text-gray-500" /> : <ChevronRight className="icon-md text-gray-500" />}
              </button>
              {expandedSections.has('toggles') && (
                <div className="p-4 bg-white grid grid-cols-2 md:grid-cols-4 gap-2">
                  <button type="button" onClick={() => handleToggle('isFavorite', !isFavorite)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-default ${isFavorite ? 'bg-pink-100 text-pink-800 border border-pink-300' : 'border-2 border-gray-300 text-gray-600 hover:bg-gray-200'}`}>
                    <Heart className={`w-4 h-4 ${isFavorite ? 'fill-pink-500' : ''}`} />
                    <span className="text-sm">Favorite</span>
                  </button>
                  <button type="button" onClick={() => handleToggle('onWishlist', !onWishlist)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-default ${onWishlist ? 'bg-amber-100 text-amber-800 border border-amber-300' : 'border-2 border-gray-300 text-gray-600 hover:bg-gray-200'}`}>
                    <Star className={`w-4 h-4 ${onWishlist ? 'fill-amber-500 text-amber-500' : ''}`} />
                    <span className="text-sm">PublicWishlist</span>
                  </button>
                  <button type="button" onClick={() => handleToggle('isRareLE', !isRareLE)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-default ${isRareLE ? `${CategoryColors.polish.light} ${CategoryColors.polish.text} border ${CategoryColors.polish.lightBorder}` : 'border-2 border-gray-300 text-gray-600 hover:bg-gray-200'}`}>
                    <AppIcons.RareLE className={`w-4 h-4 ${isRareLE ? 'fill-purple-500' : ''}`} />
                    <span className="text-sm">Rare/Limited Edition</span>
                  </button>
                  <button type="button" onClick={() => handleToggle('needsInfo', !needsInfo)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-default ${needsInfo ? 'bg-blue-100 text-blue-800 border border-blue-300' : 'border-2 border-gray-300 text-gray-600 hover:bg-gray-200'}`}>
                    <Info className={`w-4 h-4 ${needsInfo ? 'fill-blue-500' : ''}`} />
                    <span className="text-sm">Needs Info</span>
                  </button>
                  <button type="button" onClick={() => handleToggle('swatched', !swatched)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-default ${swatched ? 'bg-teal-100 text-teal-800 border border-teal-300' : 'border-2 border-gray-300 text-gray-600 hover:bg-gray-200'}`}>
                    <Eye className={`w-4 h-4 ${swatched ? 'text-teal-600' : ''}`} />
                    <span className="text-sm">Swatched</span>
                  </button>
                  <button type="button" onClick={() => handleToggle('isPrivate', !isPrivate)}
                    className={`col-span-2 md:col-span-4 flex items-center gap-2 px-3 py-2 rounded-lg transition-default ${isPrivate ? 'bg-slate-200 text-slate-800 border border-slate-400' : 'border-2 border-gray-300 text-gray-600 hover:bg-gray-200'}`}>
                    <span className="text-base">🔒</span>
                    <div className="flex-1 text-left">
                      <span className="text-sm">Private (hide from public profile)</span>
                      {isPrivate && (polish.isForDestash || isForDestash) && (
                        <div className="mt-1 text-xs text-amber-600 italic">
                          This item is also listed for Destash — buyers can still see it on Marketplace.
                        </div>
                      )}
                    </div>
                  </button>
                </div>
              )}
              
              {/* ISO Priority Selector - Only shows when on wishlist */}
              {expandedSections.has('toggles') && onWishlist && (
                <div className="px-4 pb-4 bg-white border-t border-gray-100">
                  <div className="pt-3">
                    <p className="text-xs text-gray-500 mb-2 font-medium">How badly do you want this?</p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleIsoPriorityChange('iso')}
                        className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-default ${
                          isoPriority === 'iso'
                            ? PriorityColors.iso.badge
                            : 'border-2 border-gray-300 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {PriorityColors.iso.label}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleIsoPriorityChange('diso')}
                        className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-default ${
                          isoPriority === 'diso'
                            ? PriorityColors.diso.badge
                            : 'border-2 border-gray-300 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {PriorityColors.diso.label}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleIsoPriorityChange('unicorn')}
                        className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-default ${
                          isoPriority === 'unicorn'
                            ? PriorityColors.unicorn.badge
                            : 'border-2 border-gray-300 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {PriorityColors.unicorn.label}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* MARKETPLACE LISTING SECTION - Owner only */}
            {!isPublicView && (
            <div className={`border rounded-lg overflow-hidden ${isForDestash ? 'border-green-300 bg-green-50' : 'border border-gray-300'}`}>
              <button
                type="button"
                onClick={() => toggleSection('marketplace')}
                className={`w-full flex items-center justify-between px-4 py-3 transition-default ${isForDestash ? 'bg-green-100 hover:bg-green-200' : 'border border-gray-200 hover:bg-gray-50'}`}
              >
                <div className="flex items-center gap-2">
                  <Store className={`w-5 h-5 ${isForDestash ? 'text-green-600' : 'text-gray-500'}`} />
                  <span className={`font-medium ${isForDestash ? 'text-green-800' : 'text-gray-900'}`}>
                    List for Destash
                  </span>
                  {isForDestash && (
                    <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full font-bold">LISTED</span>
                  )}
                  {destashSaved && <span className="text-green-600 text-xs ml-2">✓ Saved</span>}
                </div>
                {expandedSections.has('marketplace') ? <ChevronDown className="icon-md text-gray-500" /> : <ChevronRight className="icon-md text-gray-500" />}
              </button>
              {expandedSections.has('marketplace') && (
                <div className="p-4 bg-white space-y-4">
                  {/* Main Toggle */}
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isForDestash}
                      onChange={(e) => handleToggleDestash(e.target.checked)}
                      className="w-5 h-5 rounded text-green-500 focus:ring-green-500"
                    />
                    <span className="font-medium text-gray-600">List on Marketplace</span>
                  </label>

                  {/* Expanded fields when listing is enabled */}
                  {isForDestash && (
                    <div className="space-y-4 pt-2 border-t border border-gray-300">
                      {/* Currently Listed Status (when already saved) */}
                      {wasAlreadyListed && polish.destashStatus === 'available' && !showListedSuccess && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
                          <div className="flex items-center gap-2 text-green-700">
                            <Store className="icon-sm" />
                            <span className="font-medium text-sm">Currently listed on Marketplace</span>
                          </div>
                          <a
                            href="/marketplace?tab=destash"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-green-600 hover:text-green-800 text-sm font-medium flex items-center gap-1"
                          >
                            View
                            <ExternalLink className="icon-xs" />
                          </a>
                        </div>
                      )}

                      {/* Quantity Available - Only show if quantity > 1 */}
                      {(parseInt(quantity) || 1) > 1 && (
                        <div>
                          <label className="block text-sm font-bold text-gray-600 mb-2 font-body">
                            How many to list? <span className="text-xs text-gray-500 font-normal">(You have {quantity})</span>
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {Array.from({ length: parseInt(quantity) || 1 }, (_, i) => i + 1).map((num) => (
                              <button
                                key={num}
                                type="button"
                                onClick={() => setDestashQuantityAvailable(num)}
                                className={`w-10 h-10 rounded-lg font-bold transition-default ${
                                  destashQuantityAvailable === num
                                    ? 'bg-green-500 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                {num}
                              </button>
                            ))}
                          </div>
                          {destashQuantityAvailable < (parseInt(quantity) || 1) && (
                            <p className="mt-2 text-sm text-gray-600">
                              ✨ Listing {destashQuantityAvailable}, keeping {(parseInt(quantity) || 1) - destashQuantityAvailable}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Condition - Required */}
                      <ConditionSelector
                        itemType="polish"
                        value={destashCondition}
                        onChange={setDestashCondition}
                        required={true}
                      />

                      {/* Price - Required */}
                      <div>
                        <label className="block text-sm font-bold text-gray-600 mb-1 font-body">
                          Price <span className="text-red-500">*</span>
                        </label>
                        <div className="relative w-36">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{currency.symbol}</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={destashPrice}
                            onChange={(e) => setDestashPrice(e.target.value)}
                            placeholder="0.00"
                            className={`w-full ${symbolPad} pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${!destashPrice ? 'border-amber-300' : 'border border-gray-300'}`}
                          />
                        </div>
                        {!destashPrice && (
                          <p className="mt-1 text-xs text-amber-600">Please enter a price</p>
                        )}
                        <p className="mt-1 text-xs text-gray-700 italic">
                          Shown to buyers in <strong>{currency.label}</strong>. Change your currency in <a href="/settings" className="underline hover:text-purple-600">Settings</a>.
                        </p>
                      </div>

                      {/* Open to Swaps - Optional */}
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isOpenToSwaps}
                          onChange={(e) => setIsOpenToSwaps(e.target.checked)}
                          className="w-5 h-5 rounded text-blue-500 focus:ring-blue-500"
                        />
                        <Repeat className="icon-sm text-blue-500" />
                        <span className="font-medium text-gray-600">Open to Swaps</span>
                        <span className="text-xs text-gray-500">(optional)</span>
                      </label>

                      {/* Notes - Optional */}
                      <div>
                        <label className="block text-sm font-bold text-gray-600 mb-1 font-body">
                          Destash Notes <span className="text-xs text-gray-500 font-normal">(optional)</span>
                        </label>
                        <textarea
                          value={destashNotes}
                          onChange={(e) => setDestashNotes(e.target.value)}
                          placeholder="e.g., Only swatched once, beautiful color but doesn't suit my skin tone..."
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                        />
                      </div>

                      {/* Save Button */}
                      <button
                        type="button"
                        onClick={handleSaveDestashListing}
                        disabled={!destashCondition || !destashPrice}
                        className="w-full py-2 px-4 bg-green-500 text-white rounded-lg font-bold hover:bg-green-600 transition-default disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        <Store className="icon-sm" />
                        {wasAlreadyListed ? 'Update Listing' : 'Save & List on Marketplace'}
                      </button>

                      {/* Success Banner */}
                      {showListedSuccess && (
                        <div className="bg-green-100 border border-green-300 rounded-lg p-4">
                          <div className="flex items-center gap-2 text-green-800 font-bold mb-2">
                            <span className="text-xl">🎉</span>
                            <span>{wasAlreadyListed ? 'Listing Updated!' : 'Listed on Marketplace!'}</span>
                          </div>
                          <p className="text-sm text-green-700 mb-3">
                            Your item is now visible to other collectors.
                          </p>
                          <a
                            href="/marketplace?tab=destash"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-default text-sm"
                          >
                            <ExternalLink className="icon-sm" />
                            View in Marketplace
                          </a>
                        </div>
                      )}

                      {/* Warning message */}
                      {!showListedSuccess && (
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <AppIcons.Warning className="w-3 h-3 text-current" aria-label="Warning" />
                          This item will be visible to other users on the marketplace
                        </p>
                      )}
                    </div>
                  )}

                  {/* Remove Listing Button - shows when item is/was listed */}
                  {(wasAlreadyListed || polish.isForDestash || isForDestash) && (
                    <div className="pt-3 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={handleRemoveListingAndClose}
                        className="w-full py-2 px-4 bg-gray-100 text-gray-700 border border-gray-300 rounded-lg font-medium hover:bg-gray-200 transition-default flex items-center justify-center gap-2"
                      >
                        <X className="icon-sm" />
                        Remove Listing & Close
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            )}

          </div>

          {/* ARCHIVE & DELETE SECTION - Owner only */}
          {!isPublicView && (
          <div className="mt-6 border-t border border-gray-300 pt-6">
            {isInTrash ? (
              /* Item is in trash - show restore and permanent delete */
              <div className="border-2 border-red-200 bg-red-50 rounded-lg p-4">
                <div className="flex items-center gap-2 text-red-700 mb-3">
                  <Trash2 className="w-5 h-5" />
                  <span className="font-medium">This item is in the Trash</span>
                </div>
                <p className="text-sm text-red-600 mb-4">
                  Restore this item to return it to your collection, or delete it permanently.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    onClick={handleRestore}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-default font-medium"
                  >
                    <RefreshCw className="w-5 h-5" />
                    <span>Restore to Collection</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPermanentDeleteConfirm(true)}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-default font-medium"
                  >
                    <AppIcons.Delete className="w-5 h-5 text-current" aria-label="Delete" />
                    <span>Delete Forever</span>
                  </button>
                </div>
              </div>
            ) : polish.isDestashed ? (
              /* Already archived - show status and un-archive option */
              <div className="border-2 border-gray-300 border border-gray-300 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <AppIcons.Archive className="w-5 h-5 text-current" aria-label="Archived" />
                      <span className="font-medium">Archived</span>
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {polish.destashMethod && <span className="capitalize">{polish.destashMethod}</span>}
                      {polish.destashSalePrice && <span> for ${polish.destashSalePrice.toFixed(2)}</span>}
                      {polish.destashDate && (
                        <span> on {new Date(polish.destashDate).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleUnArchive}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-default text-sm font-medium"
                  >
                    🔄 I Got It Back!
                  </button>
                </div>
              </div>
            ) : (
              /* Not archived - show archive and delete buttons */
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => setShowArchiveModal(true)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-200 transition-default font-medium"
                >
                  <AppIcons.Archive className="w-5 h-5 text-current" aria-label="Archive" />
                  <span>Mark as Gone (Sold, Traded, etc.)</span>
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-default font-medium border border-red-200"
                >
                  <Trash2 className="w-5 h-5" />
                  <span>Move to Trash</span>
                </button>
              </div>
            )}
          </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 border border-gray-200 px-4 md:px-6 py-3 md:py-4 border-t border border-gray-300">
          <button
            type="button"
            onClick={handleClose}
            className={`w-full px-6 py-3 ${CategoryColors.polish.bgActive} text-white rounded-lg font-semibold transition-default`}
          >
            Close
          </button>
        </div>
      </div>

      {/* ARCHIVE MODAL - For when item actually leaves collection */}
      {showArchiveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="glass-modal max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <AppIcons.Archive className="w-6 h-6 text-current" aria-label="Archive" /> Archive This Polish
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Use this when the polish has left your collection (sold, traded, etc.)
            </p>
            
            <div className="space-y-4">
              {/* Quantity Selection - Only show if quantity > 1 */}
              {(polish.quantity || 1) > 1 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <label className="text-sm font-bold text-amber-800 block mb-2">
                    📦 You have {polish.quantity} of this polish
                  </label>
                  <p className="text-xs text-amber-700 mb-3">
                    How many do you want to keep?
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {Array.from({ length: (polish.quantity || 1) + 1 }, (_, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setArchiveKeepQuantity(i)}
                        className={`px-3 py-2 rounded-lg font-bold text-sm transition-all ${
                          archiveKeepQuantity === i
                            ? 'bg-amber-500 text-white ring-2 ring-amber-300'
                            : 'bg-white border border-amber-300 text-amber-700 hover:bg-amber-100'
                        }`}
                      >
                        {i === 0 ? 'Keep 0 (Archive All)' : `Keep ${i}`}
                      </button>
                    ))}
                  </div>
                  {archiveKeepQuantity > 0 && (
                    <p className="text-xs text-amber-600 mt-2">
                      ✨ {polish.quantity! - archiveKeepQuantity} will leave your collection, {archiveKeepQuantity} will remain
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-gray-600 block mb-1">How did it leave?</label>
                <select
                  value={archiveMethod}
                  onChange={(e) => setArchiveMethod(e.target.value)}
                  className={`w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`}
                >
                  <option value="sold">💵 Sold</option>
                  <option value="traded">🔄 Traded</option>
                  <option value="gifted">🎁 Gifted</option>
                  <option value="lost">Lost</option>
                  <option value="damaged">Damaged</option>
                  <option value="used-up">Used Up</option>
                  <option value="other">📝 Other</option>
                </select>
              </div>

              {archiveMethod === 'sold' && (
                <div>
                  <label className="text-sm font-medium text-gray-600 block mb-1">Sale Price (optional)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{currency.symbol}</span>
                    <input
                      type="number"
                      step="0.01"
                      value={archiveSalePrice}
                      onChange={(e) => setArchiveSalePrice(e.target.value)}
                      placeholder="0.00"
                      className={`w-full ${symbolPad} pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent`}
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-gray-600 block mb-1">Notes (optional)</label>
                <textarea
                  value={archiveNotes}
                  onChange={(e) => setArchiveNotes(e.target.value)}
                  placeholder="e.g., Traded to @nailpolishfan on Instagram"
                  rows={2}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:${CategoryColors.polish.ring} focus:border-transparent resize-none`}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowArchiveModal(false);
                  setArchiveKeepQuantity(0);
                }}
                className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-200 transition-default font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleArchive}
                className={`flex-1 px-4 py-2 ${CategoryColors.polish.bgActive} text-white rounded-lg transition-default font-medium`}
              >
                {(polish.quantity || 1) > 1 && archiveKeepQuantity > 0 
                  ? `✓ Remove ${polish.quantity! - archiveKeepQuantity} & Keep ${archiveKeepQuantity}`
                  : '✓ Archive Item'
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL (Soft delete - moves to trash) */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="glass-modal max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-red-600 mb-2 flex items-center gap-2">
              <Trash2 className="w-6 h-6" /> Move to Trash?
            </h3>
            
            <p className="text-white mb-4">
              This will move the following item to your Trash:
            </p>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="font-bold text-gray-900">{polish.name}</div>
              <div className="text-gray-600">by {polish.brand}</div>
            </div>
            
            <p className="text-amber-600 font-medium text-sm mb-6 flex items-center gap-2">
              <AppIcons.Warning className="w-4 h-4 text-current" aria-label="Warning" />
              You can restore it later from the Trash filter.
            </p>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border-2 border-slate-500 text-white rounded-lg hover:bg-slate-600 transition-default font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-default font-medium"
              >
                <Trash2 className="w-4 h-4" />
                Move to Trash
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PERMANENT DELETE CONFIRMATION MODAL */}
      {showPermanentDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="glass-modal max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-red-600 mb-2 flex items-center gap-2">
              <AppIcons.Warning className="w-6 h-6 text-current" aria-label="Warning" /> Delete Forever?
            </h3>
            
            <p className="text-white mb-4">
              This will permanently remove:
            </p>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="font-bold text-gray-900">{polish.name}</div>
              <div className="text-gray-600">by {polish.brand}</div>
            </div>
            
            <p className="text-red-600 font-medium text-sm mb-6 flex items-center gap-2">
              <AppIcons.Warning className="w-6 h-6 text-current" aria-label="Warning" />
              This cannot be undone! All data including photos will be permanently deleted.
            </p>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowPermanentDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border-2 border-slate-500 text-white rounded-lg hover:bg-slate-600 transition-default font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePermanentDelete}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-default font-medium"
              >
                <AppIcons.Delete className="w-4 h-4 text-current" aria-label="Delete" />
                Yes, Delete Forever
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Storage Upgrade Modal */}
      {showStorageUpgradeModal && (
        <StorageUpgradeModal
          isOpen={showStorageUpgradeModal}
          onClose={() => setShowStorageUpgradeModal(false)}
        />
      )}
    </div>
  );
}
